/**
 * Ontology page
 * -------------
 * Three surfaces (tabbed):
 *   1. Versions — every OntologyVersion in the workspace.
 *      Each row offers: Card (markdown drawer with mini-graph) and
 *      Export ▾ (JSON-LD, Turtle, OWL/XML, Cypher, CSV bundle, graph.json).
 *   2. Generate from domain — POST /api/v1/ontology/generate/.
 *   3. Start from standard — inspect source policy, then reference, align,
 *      or seed a DRAFT only when that exact standard permits the mode.
 *
 * Drafts never auto-publish; the eval gate stays intact.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BadgeCheck, BookOpen, CheckCircle2, Download, ExternalLink, FileUp, GitPullRequestArrow,
  GitBranch, Hexagon, Languages, Library, ListChecks, Loader2, Play, Save,
  ChevronDown, FileCode2, Pencil, Plus, Printer, RotateCcw, ShieldCheck, Sparkles,
  Terminal, Trash2, UploadCloud, Wand2, X, XCircle,
} from "lucide-react";
import { downloadBlob, rawFetch, rawMultipart } from "@/lib/api/client";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { PageHeader, ErrorBox, EmptyState, StatusPill } from "@/components/common";
import { Markdown } from "@/components/Markdown";
import { OntologyMiniGraph, type OntologyGraph } from "@/components/OntologyMiniGraph";
import { OntologyGraphExplorer } from "@/components/OntologyGraphExplorer";
import { PaginatedTable } from "@/components/PaginatedTable";
import { TermSetSelector } from "@/components/TermSetSelector";
import { EligiblePackSelector } from "@/components/EligiblePackSelector";
import { ReleaseReadinessPanel } from "@/components/ReleaseReadinessPanel";

interface OntologyVersion {
  id: string;
  workspace?: string;
  semver: string;
  status: string;
  created_at?: string;
  review_required?: boolean;
  review_draft_nodes?: { iri: string; label?: string; note: string }[];
  review_completed_by?: string;
  review_completed_at?: string;
  review_notes?: string;
  structured_review_enabled?: boolean;
}

interface StructuredReviewAssignment {
  id: string;
  role: string;
  assigned_to?: string;
  required: boolean;
  status: string;
  conditions: Array<{ text?: string; satisfied?: boolean }>;
  rationale?: string;
  reviewed_by?: string;
  reviewed_at?: string;
}

interface StructuredReviewItem {
  id: string;
  category: string;
  subject_iri?: string;
  source?: string;
  risk_level: string;
  summary?: string;
  required: boolean;
  disposition?: string;
  rationale?: string;
  mapping_strength?: string;
  approval_condition?: string;
  reviewer?: string;
  reviewed_at?: string;
  change_proposal?: string | null;
  child_version?: string | null;
  rdf_diff?: Record<string, unknown>;
}

interface StructuredReviewResponse {
  assignments: StructuredReviewAssignment[];
  items: StructuredReviewItem[];
  release_policy: ReleasePolicyReport;
}

interface TranslationTerm {
  subject_iri: string;
  predicate: string;
  language: string;
  value: string;
}

interface TranslationProfile {
  id: string;
  name: string;
  description: string;
  default_language: string;
  default_language_display: string;
  languages: string[];
  status: string;
  provenance: Record<string, unknown>;
  approved_by?: string;
  terms: TranslationTerm[];
}

interface GenerateResponse {
  version_id: string;
  status: string;
  semver: string;
  entities_created: number;
  relations_created: number;
  taxonomies_created: number;
}

interface StandardSummary {
  id: string;
  display_name: string;
  industry: string;
  description: string;
  source_url: string;
  license: string;
  scope: "bundled" | "external";
  entity_count: number;
  relation_count: number;
  taxonomy_count: number;
  integration_notes?: string;
  availability: string;
  seedable: boolean;
  unavailable_reason?: string;
}

const INDUSTRIES: { id: string; label: string }[] = [
  { id: "",              label: "General — universal core only" },
  { id: "insurance",     label: "Insurance" },
  { id: "finance",       label: "Finance" },
  { id: "banking",       label: "Banking" },
  { id: "pharma",        label: "Pharma & life sciences" },
  { id: "healthcare",    label: "Healthcare" },
  { id: "legal",         label: "Legal" },
  { id: "tax",           label: "Tax" },
  { id: "retail",        label: "Retail / e-commerce" },
  { id: "manufacturing", label: "Manufacturing" },
  { id: "telecom",       label: "Telecom" },
  { id: "energy",        label: "Energy / utilities" },
  { id: "public_sector", label: "Public sector" },
  { id: "logistics",     label: "Logistics" },
  { id: "real_estate",   label: "Real estate" },
  { id: "education",     label: "Education" },
];

const EXPORT_FORMATS: { fmt: string; label: string; description: string; ext: string }[] = [
  { fmt: "jsonld",     label: "JSON-LD",    description: "Linked-data exchange", ext: "jsonld" },
  { fmt: "turtle",     label: "Turtle",     description: "Readable RDF source", ext: "ttl"    },
  { fmt: "owl",        label: "OWL / XML",  description: "Ontology tool import", ext: "owl"    },
  { fmt: "cypher",     label: "Cypher",     description: "Property-graph schema", ext: "cypher" },
  { fmt: "csv",        label: "CSV bundle", description: "Tabular archive", ext: "zip"    },
  { fmt: "graph-json", label: "Graph JSON", description: "Node and edge graph", ext: "json"   },
];

type Tab = "versions" | "proposals" | "candidates" | "translations" | "generate" | "standards";

export function Ontology() {
  const ws = useApp((s) => s.workspaceId);
  const { canEdit, canAdmin, isLoading: roleLoading, error: roleError } = useWorkspacePermissions();
  const [tab, setTab] = useState<Tab>("versions");
  const [cardVersionId, setCardVersionId] = useState<string | null>(null);
  const [validateVersionId, setValidateVersionId] = useState<string | null>(null);
  const [reviewVersionId, setReviewVersionId] = useState<string | null>(null);

  useEffect(() => {
    const pending = sessionStorage.getItem("ckos.ontology.openValidate");
    if (pending) {
      sessionStorage.removeItem("ckos.ontology.openValidate");
      setValidateVersionId(pending);
    }
  }, []);

  if (!canEdit) {
    return <>
      <PageHeader title="Ontology" subtitle="Versioned entity and relation schemas." />
      <div className="card mb-4 text-xs text-muted" role="status">
        {roleLoading ? "Checking your workspace role…" : roleError ?
          "Unable to verify your workspace role. Editing is unavailable." :
          "This workspace is read-only for your role. An editor or admin can author ontology drafts."}
      </div>
      {ws && <PaginatedTable<OntologyVersion>
        endpoint="/ontology/versions/"
        baseParams={{ workspace: ws }}
        cols={[
          { header: "Semver", cell: (version) => <code>{version.semver}</code> },
          { header: "Status", cell: (version) => <StatusPill status={version.status} /> },
          { header: "ID", cell: (version) => <code>{version.id.slice(0, 8)}</code> },
        ]}
        empty="No ontology versions yet."
      />}
    </>;
  }

  return (
    <>
      <PageHeader
        title="Ontology"
        subtitle="Versioned entity & relation schemas."
        help={
          <>
            <p>Generate an Ontoligent-authored schema or adopt an external source through its supported reference, alignment, or import mode. Rights and provenance gates apply before source content is materialised. The published version is the <strong>extraction contract</strong> and exports to OWL / SKOS / ORB.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "ontology", label: "Working with ontologies" }}
        actions={canEdit ?
          <Link to="/ontology/new" className="btn-primary inline-flex items-center gap-1 text-xs">
            <Sparkles size={12} /> New (guided wizard)
          </Link>
        : undefined}
      />

      {!roleLoading && !canEdit && (
        <div className="card mb-4 text-xs text-muted" role="status">
          {roleError ? "Unable to verify your workspace role. Editing is unavailable." :
            "Ontology authoring is read-only for your role. Ask a workspace editor to create a draft."}
        </div>
      )}

      {!ws && (
        <div className="card mb-4 text-sm">
          Pick a workspace from the header dropdown first. Ontology is per-workspace.
        </div>
      )}

      <div className="mb-4 flex items-center gap-1 border-b border-border">
        <TabButton active={tab === "versions"}   onClick={() => setTab("versions")}   icon={<Hexagon size={14} />}>Versions</TabButton>
        <TabButton active={tab === "proposals"}  onClick={() => setTab("proposals")}  icon={<GitPullRequestArrow size={14} />}>Change proposals</TabButton>
        {canAdmin && <TabButton active={tab === "candidates"} onClick={() => setTab("candidates")} icon={<ListChecks size={14} />}>Extraction review</TabButton>}
        <TabButton active={tab === "translations"} onClick={() => setTab("translations")} icon={<Languages size={14} />}>Translation library</TabButton>
        <TabButton active={tab === "generate"}   onClick={() => setTab("generate")}   icon={<Wand2 size={14} />}>Generate from domain</TabButton>
        <TabButton active={tab === "standards"}  onClick={() => setTab("standards")}  icon={<Library size={14} />}>Start from standard</TabButton>
      </div>

      {tab === "versions"  && <VersionsTab ws={ws} onShowCard={setCardVersionId} onValidate={setValidateVersionId} onReview={setReviewVersionId} />}
      {tab === "proposals" && <ChangeProposalsTab ws={ws} onApproved={() => setTab("versions")} />}
      {tab === "candidates" && <ExtractionCandidatesTab ws={ws} />}
      {tab === "translations" && <TranslationLibraryTab ws={ws} />}
      {tab === "generate"  && <GenerateTab ws={ws} canEdit={canEdit} onCreated={() => setTab("versions")} />}
      {tab === "standards" && <StandardsTab ws={ws} onCreated={() => setTab("versions")} />}

      {cardVersionId && (
        <CardDrawer versionId={cardVersionId} onClose={() => setCardVersionId(null)} />
      )}

      {validateVersionId && (
        <ValidateDrawer versionId={validateVersionId} onClose={() => setValidateVersionId(null)} />
      )}

      {reviewVersionId && (
        <StructuredReviewDrawer versionId={reviewVersionId} onClose={() => setReviewVersionId(null)} />
      )}
    </>
  );
}

interface ExtractionCandidate {
  id: string;
  kind: "entity" | "relation";
  entity_type: string;
  relation_type: string;
  natural_key: string;
  src_natural_key: string;
  dst_natural_key: string;
  evidence_text: string;
  status: "pending" | "approved" | "rejected";
  review_note: string;
  created_at?: string;
}

function ExtractionCandidatesTab({ ws }: { ws: string | null }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState("pending");
  const [selected, setSelected] = useState<ExtractionCandidate | null>(null);
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (!selected) return;
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") setSelected(null); };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [selected]);
  const candidates = useQuery<{ results: ExtractionCandidate[] }>({
    queryKey: ["semantic-candidates", ws, status],
    queryFn: () => rawFetch(`/semantic/candidates/?workspace=${ws}&status=${status}&page_size=50`),
    enabled: !!ws,
  });
  const decide = useMutation({
    mutationFn: (decision: "approve" | "reject") => rawFetch(
      `/semantic/candidates/${selected!.id}/${decision}/`,
      { method: "POST", body: JSON.stringify({ reason: reason.trim() }) },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["semantic-candidates", ws] });
      setSelected(null);
      setReason("");
    },
  });
  if (!ws) return <EmptyState title="Select a workspace first." />;
  return <section className="space-y-3">
    <div className="card text-sm">
      <h2 className="font-semibold">Review extracted instances</h2>
      <p className="mt-1 text-muted">These are evidence-backed entity and relationship candidates from searchable documents. They are not ontology classes or change proposals. Only a workspace admin with access to the source document can see and decide them. Approval makes a candidate eligible for a later graph-writing pipeline run; it does not publish a graph or change the ontology.</p>
    </div>
    <label className="block text-xs text-muted">Review status
      <select className="input mt-1 max-w-xs" value={status} onChange={(event) => { setStatus(event.target.value); setSelected(null); }}>
        <option value="pending">Needs review</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
      </select>
    </label>
    {candidates.isError && <ErrorBox error={candidates.error} />}
    {candidates.isLoading && <p role="status" className="text-sm text-muted">Loading candidates…</p>}
    {!candidates.isLoading && !candidates.isError && !(candidates.data?.results.length) &&
      <EmptyState title="No accessible candidates in this status." hint="Run an extraction pipeline on a searchable source, or choose another status. Document permissions also apply." />}
    {(candidates.data?.results ?? []).map((candidate) => <button key={candidate.id} type="button"
      className="card block w-full text-left hover:border-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      onClick={() => { setSelected(candidate); setReason(""); }}>
      <span className="text-sm font-semibold">{candidate.kind === "entity" ?
        `${candidate.entity_type}: ${candidate.natural_key}` :
        `${candidate.src_natural_key} → ${candidate.relation_type} → ${candidate.dst_natural_key}`}</span>
      <span className="ml-2 text-xs text-muted">{candidate.status}</span>
      <span className="mt-1 block truncate text-xs text-muted">{candidate.evidence_text}</span>
    </button>)}
    {selected && <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={() => setSelected(null)}>
      <aside className="flex h-full w-full max-w-xl flex-col border-l border-border bg-card p-5 shadow-xl" role="dialog" aria-modal="true" aria-label="Extraction candidate review" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between gap-2"><h2 className="text-lg font-semibold">Review {selected.kind}</h2>
          <button type="button" className="btn" aria-label="Close review" onClick={() => setSelected(null)}><X size={16} /></button></div>
        <div className="mt-5 min-h-0 flex-1 space-y-4 overflow-y-auto text-sm">
          <div><div className="text-xs uppercase text-muted">Candidate</div><p className="mt-1 font-medium">{selected.kind === "entity" ?
            `${selected.entity_type}: ${selected.natural_key}` :
            `${selected.src_natural_key} → ${selected.relation_type} → ${selected.dst_natural_key}`}</p></div>
          <div><div className="text-xs uppercase text-muted">Source evidence</div><p className="mt-1 whitespace-pre-wrap border border-border bg-bg/50 p-3">{selected.evidence_text}</p></div>
          {selected.status === "pending" ? <label className="block text-xs text-muted">Review reason (required)
            <textarea className="input mt-1 min-h-24 w-full" maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Explain the decision" />
          </label> : <p className="text-muted">Decision: {selected.status}. {selected.review_note}</p>}
          {decide.isError && <ErrorBox error={decide.error} />}
        </div>
        {selected.status === "pending" && <div className="flex gap-2 border-t border-border pt-4">
          <button type="button" className="btn-primary" disabled={!reason.trim() || decide.isPending} onClick={() => decide.mutate("approve")}>Approve candidate</button>
          <button type="button" className="btn" disabled={!reason.trim() || decide.isPending} onClick={() => decide.mutate("reject")}>Reject candidate</button>
        </div>}
      </aside>
    </div>}
  </section>;
}

function TranslationLibraryTab({ ws }: { ws: string | null }) {
  const { canAdmin } = useWorkspacePermissions();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TranslationProfile | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultLanguage, setDefaultLanguage] = useState("en");
  const [languages, setLanguages] = useState("en, es");
  const [provenance, setProvenance] = useState<Record<string, string>>({ translator: "", source: "", review_notes: "" });
  const [terms, setTerms] = useState<TranslationTerm[]>([]);
  const [targetVersion, setTargetVersion] = useState("");

  const profiles = useQuery<{ results: TranslationProfile[] }>({
    queryKey: ["translation-profiles", ws],
    queryFn: () => rawFetch(`/ontology/translation-profiles/?workspace=${ws}&page_size=100`),
    enabled: !!ws,
  });
  const versions = useQuery<{ results: OntologyVersion[] }>({
    queryKey: ["ontology-versions", ws, "translation-targets"],
    queryFn: () => rawFetch(`/ontology/versions/?workspace=${ws}&status=draft&page_size=100`),
    enabled: !!ws,
  });
  const subjects = useQuery<{ results: Array<{ name: string; semantic_iri: string; kind: string }> }>({
    queryKey: ["ontology-translation-subjects", targetVersion],
    queryFn: () => rawFetch(`/ontology/versions/${targetVersion}/lexical-subjects/`),
    enabled: !!targetVersion,
  });

  const reset = () => {
    setEditing(null); setName(""); setDescription(""); setDefaultLanguage("en");
    setLanguages("en, es");
    setProvenance({ translator: "", source: "", review_notes: "" });
    setTerms([]);
  };
  const edit = (profile: TranslationProfile) => {
    setEditing(profile); setName(profile.name); setDescription(profile.description);
    setDefaultLanguage(profile.default_language); setLanguages(profile.languages.join(", "));
    setProvenance({ translator: String(profile.provenance?.translator ?? ""), source: String(profile.provenance?.source ?? ""), review_notes: String(profile.provenance?.review_notes ?? "") });
    setTerms(profile.terms.map(({ subject_iri, predicate, language, value }) => ({ subject_iri, predicate, language, value })));
  };
  const copy = (profile: TranslationProfile) => {
    edit(profile);
    setEditing(null);
    setName(`${profile.name} copy`);
  };
  const updateTerm = (index: number, patch: Partial<TranslationTerm>) =>
    setTerms((current) => current.map((term, position) => position === index ? { ...term, ...patch } : term));

  const save = useMutation({
    mutationFn: () => rawFetch(`/ontology/translation-profiles/${editing ? `${editing.id}/` : ""}`, {
      method: editing ? "PUT" : "POST",
      body: JSON.stringify({
        workspace: ws, name, description, default_language: defaultLanguage,
        languages: languages.split(",").map((item) => item.trim()).filter(Boolean),
        provenance, terms,
      }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["translation-profiles", ws] });
      reset();
    },
  });
  const approve = useMutation({
    mutationFn: (id: string) => rawFetch(`/ontology/translation-profiles/${id}/approve/`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["translation-profiles", ws] }),
  });
  const apply = useMutation({
    mutationFn: (id: string) => rawFetch<{ created: number; existing: number }>(`/ontology/translation-profiles/${id}/apply/`, {
      method: "POST", body: JSON.stringify({ version: targetVersion }),
    }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => rawFetch(`/ontology/translation-profiles/${id}/`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["translation-profiles", ws] }),
  });
  const retire = useMutation({
    mutationFn: (id: string) => rawFetch(`/ontology/translation-profiles/${id}/retire/`, { method: "POST" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["translation-profiles", ws] }),
  });

  if (!ws) return <EmptyState title="Select a workspace first." />;
  if (profiles.isError) return <ErrorBox error={profiles.error} />;
  const rows = profiles.data?.results ?? [];

  return (
    <div className="space-y-4">
      <div className="card border-accent/30 bg-accent/5 text-xs text-muted">
        Reuse SME-reviewed labels, aliases, and definitions across drafts. Choose a draft to pick its concepts, add language rows, then ask a workspace admin or owner to approve the library. An approved library stays unchanged; copy it to revise the wording.
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.8fr)]">
        <div className="space-y-3">
          {profiles.isLoading ? <div className="text-sm text-muted">Loading translation profiles...</div> : rows.length === 0 ? <EmptyState title="No translation profiles yet." /> : rows.map((profile) => (
            <div key={profile.id} className="card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div><div className="font-semibold text-fg">{profile.name}</div><div className="mt-1 text-xs text-muted">{profile.description}</div></div>
                <StatusPill status={profile.status} />
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                <span className="pill border-accent/30 text-accent">{profile.default_language_display}</span>
                {profile.languages.filter((language) => language !== profile.default_language).map((language) => <span key={language} className="pill">{language}</span>)}
                <span className="pill">{profile.terms.length} terms</span>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {profile.status === "draft" && <button className="btn" onClick={() => edit(profile)}><Pencil size={13} className="mr-1 inline" /> Edit</button>}
                <button className="btn" onClick={() => copy(profile)}>Copy as draft</button>
                {canAdmin && profile.status === "draft" && <button className="btn" disabled={approve.isPending} onClick={() => approve.mutate(profile.id)}><BadgeCheck size={13} className="mr-1 inline" /> Approve</button>}
                {profile.status === "approved" && <button className="btn-primary" disabled={!targetVersion || apply.isPending} onClick={() => apply.mutate(profile.id)}><Languages size={13} className="mr-1 inline" /> Apply to draft</button>}
                {profile.status === "draft" && <button className="btn text-bad" disabled={remove.isPending} onClick={() => { if (window.confirm(`Delete draft library ${profile.name}?`)) remove.mutate(profile.id); }}><Trash2 size={13} className="mr-1 inline" /> Delete draft</button>}
                {canAdmin && profile.status === "approved" && <button className="btn" disabled={retire.isPending} onClick={() => { if (window.confirm(`Retire ${profile.name}? Existing draft labels remain.`)) retire.mutate(profile.id); }}>Retire</button>}
              </div>
            </div>
          ))}
          <label className="block text-xs text-muted">Target DRAFT version
            <select className="input mt-1" value={targetVersion} onChange={(event) => setTargetVersion(event.target.value)}><option value="">Select a draft</option>{(versions.data?.results ?? []).map((version) => <option key={version.id} value={version.id}>{version.semver}</option>)}</select>
          </label>
          {apply.data && <div className="text-xs text-good">Applied: {apply.data.created} created, {apply.data.existing} already present.</div>}
          {apply.isError && <ErrorBox error={apply.error} />}
          {(approve.isError || remove.isError || retire.isError) && <ErrorBox error={approve.error ?? remove.error ?? retire.error} />}
        </div>
        <div className="card space-y-3 p-4">
          <div className="font-semibold text-fg">{editing ? `Edit ${editing.name}` : "New translation profile"}</div>
          <input className="input" placeholder="Profile name" value={name} onChange={(event) => setName(event.target.value)} />
          <textarea className="input min-h-16" placeholder="Description" value={description} onChange={(event) => setDescription(event.target.value)} />
          <div className="grid gap-2 sm:grid-cols-2"><label className="text-xs text-muted">Default language<input className="input mt-1" value={defaultLanguage} onChange={(event) => setDefaultLanguage(event.target.value)} /></label><label className="text-xs text-muted">Supported languages<input className="input mt-1" value={languages} onChange={(event) => setLanguages(event.target.value)} /></label></div>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="text-xs text-muted">Reviewed by<input className="input mt-1" value={provenance.translator ?? ""} onChange={(event) => setProvenance((current) => ({ ...current, translator: event.target.value }))} /></label>
            <label className="text-xs text-muted">Source<input className="input mt-1" value={provenance.source ?? ""} onChange={(event) => setProvenance((current) => ({ ...current, source: event.target.value }))} /></label>
          </div>
          <label className="block text-xs text-muted">Review notes<textarea className="input mt-1 min-h-16" value={provenance.review_notes ?? ""} onChange={(event) => setProvenance((current) => ({ ...current, review_notes: event.target.value }))} /></label>
          <div className="border-t border-border pt-3">
            <div className="mb-2 flex items-center justify-between gap-2"><div><h3 className="text-sm font-semibold">Language entries</h3><p className="text-xs text-muted">Labels, aliases, and definitions are separate rows. Nothing is translated automatically.</p></div><button className="btn text-xs" type="button" onClick={() => setTerms((current) => [...current, { subject_iri: "", predicate: "label", language: defaultLanguage.trim() || "en", value: "" }])}><Plus size={12} /> Add entry</button></div>
            {terms.length === 0 && <p className="py-3 text-xs text-muted">Add an entry or copy an existing library to begin.</p>}
            <div className="max-h-[460px] space-y-2 overflow-y-auto">
              {terms.map((term, index) => <div key={index} className="border border-border bg-bg/40 p-2">
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_115px_90px_auto]">
                  <label className="text-[11px] text-muted">Concept
                    <input className="input mt-1 text-xs" list={`translation-subjects-${index}`} placeholder="Choose a draft concept or enter its IRI" value={term.subject_iri} onChange={(event) => updateTerm(index, { subject_iri: event.target.value })} />
                    <datalist id={`translation-subjects-${index}`}>{(subjects.data?.results ?? []).map((subject) => <option key={subject.semantic_iri} value={subject.semantic_iri}>{subject.name} · {subject.kind}</option>)}</datalist>
                  </label>
                  <label className="text-[11px] text-muted">Kind<select className="input mt-1 text-xs" value={term.predicate} onChange={(event) => updateTerm(index, { predicate: event.target.value })}><option value="label">Label</option><option value="pref_label">Preferred label</option><option value="alt_label">Alias</option><option value="definition">Definition</option><option value="comment">Description</option></select></label>
                  <label className="text-[11px] text-muted">Language<input className="input mt-1 text-xs" value={term.language} onChange={(event) => updateTerm(index, { language: event.target.value })} placeholder="es" /></label>
                  <button className="btn self-end text-bad" type="button" aria-label={`Remove entry ${index + 1}`} onClick={() => setTerms((current) => current.filter((_, position) => position !== index))}><Trash2 size={13} /></button>
                </div>
                <label className="mt-2 block text-[11px] text-muted">Wording<input className="input mt-1 text-xs" value={term.value} onChange={(event) => updateTerm(index, { value: event.target.value })} placeholder="Approved wording" /></label>
              </div>)}
            </div>
          </div>
          <div className="flex gap-2"><button className="btn-primary" disabled={!name || save.isPending} onClick={() => save.mutate()}><Save size={13} className="mr-1 inline" /> Save profile</button>{editing && <button className="btn" onClick={reset}>Cancel</button>}</div>
          {save.isError && <ErrorBox error={save.error} />}
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Tabs                                                                        //
// --------------------------------------------------------------------------- //

function TabButton({
  active, onClick, icon, children,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors " +
        (active
          ? "border-accent text-fg"
          : "border-transparent text-muted hover:text-fg")
      }
    >
      {icon}
      {children}
    </button>
  );
}

// --------------------------------------------------------------------------- //
// Change proposals tab                                                        //
// --------------------------------------------------------------------------- //

interface ChangeProposal {
  id: string;
  title: string;
  rationale?: string;
  proposed_by?: string;
  base_version?: string | null;
  run_id?: string | null;
  changes: {
    add_entity_types?: { name: string; description?: string; parent?: string }[];
    add_relation_types?: { name: string; source: string; target: string; cardinality?: string; description?: string }[];
    add_properties?: { entity: string; property: string; schema?: { type?: string; description?: string } }[];
    discovery_claim?: { kind?: string; claim?: { name?: string; definition?: string; data_entity?: string } };
  };
  status: string;
  created_at?: string;
}

function ChangeProposalsTab({ ws, onApproved }: { ws: string | null; onApproved: () => void }) {
  const qc = useQueryClient();
  const { canAdmin } = useWorkspacePermissions();
  const [notice, setNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("proposed");
  const [editing, setEditing] = useState<ChangeProposal | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [rationale, setRationale] = useState("");
  const [baseVersion, setBaseVersion] = useState("");
  const [entities, setEntities] = useState<NonNullable<ChangeProposal["changes"]["add_entity_types"]>>([]);
  const [relationships, setRelationships] = useState<NonNullable<ChangeProposal["changes"]["add_relation_types"]>>([]);
  const [properties, setProperties] = useState<NonNullable<ChangeProposal["changes"]["add_properties"]>>([]);

  const proposals = useQuery<{ results: ChangeProposal[] }>({
    queryKey: ["change-proposals", ws, statusFilter],
    queryFn: () =>
      rawFetch(
        `/ontology/change-proposals/?status=${statusFilter}&page_size=50${ws ? `&workspace=${ws}` : ""}`,
      ),
    enabled: !!ws,
  });
  const versions = useQuery<{ results: OntologyVersion[] }>({
    queryKey: ["ontology-proposal-bases", ws],
    queryFn: () => rawFetch(`/ontology/versions/?workspace=${ws}&status=published&page_size=100`),
    enabled: !!ws,
  });
  const baseEntities = useQuery<{ results: EntityRow[] }>({
    queryKey: ["ontology-proposal-base-entities", baseVersion],
    queryFn: () => rawFetch(`/ontology/entities/?version=${baseVersion}&page_size=500`),
    enabled: !!baseVersion,
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["change-proposals", ws] });
  const openEditor = (proposal?: ChangeProposal) => {
    setEditing(proposal ?? null);
    setTitle(proposal?.title ?? "");
    setRationale(proposal?.rationale ?? "");
    setBaseVersion(proposal?.base_version ?? "");
    setEntities(proposal?.changes.add_entity_types?.map((row) => ({ ...row })) ?? []);
    setRelationships(proposal?.changes.add_relation_types?.map((row) => ({ ...row })) ?? []);
    setProperties(proposal?.changes.add_properties?.map((row) => ({ ...row, schema: { ...row.schema } })) ?? []);
    setEditorOpen(true);
  };
  const save = useMutation({
    mutationFn: () => rawFetch(`/ontology/change-proposals/${editing ? `${editing.id}/` : ""}`, {
      method: editing ? "PATCH" : "POST",
      body: JSON.stringify({ ...(editing ? {} : { workspace: ws }), title: title.trim(), rationale: rationale.trim(),
        base_version: baseVersion || null,
        changes: { add_entity_types: entities.filter((row) => row.name.trim()),
          add_relation_types: relationships.filter((row) => row.name.trim()),
          add_properties: properties.filter((row) => row.entity.trim() && row.property.trim()) } }),
    }),
    onSuccess: () => { setEditorOpen(false); invalidate(); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => rawFetch(`/ontology/change-proposals/${id}/`, { method: "DELETE" }),
    onSuccess: invalidate,
  });

  const approve = useMutation({
    mutationFn: (id: string) =>
      rawFetch<{ draft_version?: string }>(`/ontology/change-proposals/${id}/approve/`, {
        method: "POST",
      }),
    onSuccess: (res) => {
      invalidate();
      setNotice(
        res?.draft_version
          ? `Approved. A new DRAFT version was created — review and publish it on the Versions tab.`
          : `Approved.`,
      );
    },
  });

  const reject = useMutation({
    mutationFn: (id: string) =>
      rawFetch(`/ontology/change-proposals/${id}/reject/`, { method: "POST" }),
    onSuccess: invalidate,
  });

  if (!ws) return <EmptyState title="Select a workspace first." />;
  if (proposals.isError) return <ErrorBox error={proposals.error} />;
  if (proposals.isLoading) return <div className="text-sm text-muted">Loading…</div>;

  const rows = proposals.data?.results ?? [];
  const incomplete = entities.some((row) => !row.name.trim()) ||
    relationships.some((row) => !row.name.trim() || !row.source.trim() || !row.target.trim()) ||
    properties.some((row) => !row.entity.trim() || !row.property.trim());
  const classNames = [...new Set([...(baseEntities.data?.results ?? []).map((row) => row.name),
    ...entities.map((row) => row.name).filter(Boolean)])];

  return (
    <div className="space-y-3">
      <div className="card border-accent/30 bg-accent/5 text-xs text-muted">
        Agents and the Studio can <span className="text-fg">propose</span> additive ontology
        changes here, but never mutate the live ontology. Approving materialises the base version
        plus the additions into a new <span className="text-fg">DRAFT</span> — which still has to
        pass the eval-gated publish. Nothing is auto-published.
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="text-xs text-muted">Show proposals
          <select className="input ml-2 w-auto" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            <option value="proposed">Awaiting decision</option><option value="applied">Applied to draft</option><option value="rejected">Rejected</option>
          </select>
        </label>
        <button className="btn-primary text-xs" onClick={() => openEditor()}><Plus size={13} /> New ontology change</button>
      </div>

      {editorOpen && <section className="card space-y-3" aria-label="Ontology change editor">
        <div className="flex items-center justify-between"><h3 className="font-semibold">{editing ? "Edit proposed change" : "Propose an ontology change"}</h3><button className="btn p-1" onClick={() => setEditorOpen(false)} aria-label="Close editor"><X size={14} /></button></div>
        <p className="text-xs text-muted">Add classes, relationships between classes, or data properties. Admin approval creates a new draft and never publishes it.</p>
        <div className="grid gap-2 sm:grid-cols-2"><label className="text-xs text-muted">Title<input className="input mt-1" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="What should change?" /></label><label className="text-xs text-muted">Based on published version<select className="input mt-1" value={baseVersion} onChange={(event) => setBaseVersion(event.target.value)}><option value="">Latest published, if any</option>{(versions.data?.results ?? []).map((version) => <option value={version.id} key={version.id}>{version.semver}</option>)}</select></label></div>
        <label className="block text-xs text-muted">Why is this needed?<textarea className="input mt-1 min-h-16" value={rationale} onChange={(event) => setRationale(event.target.value)} placeholder="Business reason and source" /></label>
        <datalist id="proposal-class-names">{classNames.map((className) => <option key={className} value={className} />)}</datalist>
        <div className="grid gap-3 xl:grid-cols-3">
          <div className="border border-border p-3"><div className="mb-2 flex justify-between"><h4 className="text-sm font-semibold">Classes</h4><button className="btn text-xs" onClick={() => setEntities((rows) => [...rows, { name: "", description: "" }])}><Plus size={12} /> Add</button></div>
            {entities.map((row, index) => <div key={index} className="mb-2 space-y-1 border-b border-border pb-2"><div className="flex gap-1"><input className="input text-xs" aria-label={`Class ${index + 1} name`} placeholder="Class name" value={row.name} onChange={(event) => setEntities((rows) => rows.map((item, position) => position === index ? { ...item, name: event.target.value } : item))} /><button className="btn text-bad" aria-label={`Remove class ${index + 1}`} onClick={() => setEntities((rows) => rows.filter((_, position) => position !== index))}><Trash2 size={12} /></button></div><input className="input text-xs" aria-label={`Class ${index + 1} definition`} placeholder="Definition" value={row.description ?? ""} onChange={(event) => setEntities((rows) => rows.map((item, position) => position === index ? { ...item, description: event.target.value } : item))} /></div>)}
          </div>
          <div className="border border-border p-3"><div className="mb-2 flex justify-between"><h4 className="text-sm font-semibold">Relationships</h4><button className="btn text-xs" onClick={() => setRelationships((rows) => [...rows, { name: "", source: "", target: "", cardinality: "N-N" }])}><Plus size={12} /> Add</button></div>
            {relationships.map((row, index) => <div key={index} className="mb-2 space-y-1 border-b border-border pb-2"><div className="flex gap-1"><input className="input text-xs" aria-label={`Relationship ${index + 1} name`} placeholder="Relationship name" value={row.name} onChange={(event) => setRelationships((rows) => rows.map((item, position) => position === index ? { ...item, name: event.target.value } : item))} /><button className="btn text-bad" aria-label={`Remove relationship ${index + 1}`} onClick={() => setRelationships((rows) => rows.filter((_, position) => position !== index))}><Trash2 size={12} /></button></div><div className="grid grid-cols-2 gap-1"><input className="input text-xs" list="proposal-class-names" aria-label={`Relationship ${index + 1} source`} placeholder="From class" value={row.source} onChange={(event) => setRelationships((rows) => rows.map((item, position) => position === index ? { ...item, source: event.target.value } : item))} /><input className="input text-xs" list="proposal-class-names" aria-label={`Relationship ${index + 1} target`} placeholder="To class" value={row.target} onChange={(event) => setRelationships((rows) => rows.map((item, position) => position === index ? { ...item, target: event.target.value } : item))} /></div></div>)}
          </div>
          <div className="border border-border p-3"><div className="mb-2 flex justify-between"><h4 className="text-sm font-semibold">Data properties</h4><button className="btn text-xs" onClick={() => setProperties((rows) => [...rows, { entity: "", property: "", schema: { type: "string" } }])}><Plus size={12} /> Add</button></div>
            {properties.map((row, index) => <div key={index} className="mb-2 space-y-1 border-b border-border pb-2"><div className="flex gap-1"><input className="input text-xs" list="proposal-class-names" aria-label={`Property ${index + 1} class`} placeholder="Class" value={row.entity} onChange={(event) => setProperties((rows) => rows.map((item, position) => position === index ? { ...item, entity: event.target.value } : item))} /><button className="btn text-bad" aria-label={`Remove property ${index + 1}`} onClick={() => setProperties((rows) => rows.filter((_, position) => position !== index))}><Trash2 size={12} /></button></div><input className="input text-xs" aria-label={`Property ${index + 1} name`} placeholder="Property name" value={row.property} onChange={(event) => setProperties((rows) => rows.map((item, position) => position === index ? { ...item, property: event.target.value } : item))} /><select className="input text-xs" aria-label={`Property ${index + 1} type`} value={row.schema?.type ?? "string"} onChange={(event) => setProperties((rows) => rows.map((item, position) => position === index ? { ...item, schema: { ...item.schema, type: event.target.value } } : item))}><option value="string">Text</option><option value="integer">Whole number</option><option value="number">Number</option><option value="boolean">True or false</option></select></div>)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2"><button className="btn-primary" disabled={save.isPending || !title.trim() || incomplete || entities.length + relationships.length + properties.length === 0} onClick={() => save.mutate()}><Save size={13} /> {save.isPending ? "Saving…" : editing ? "Save changes" : "Stage proposal"}</button><button className="btn" onClick={() => setEditorOpen(false)}>Cancel</button></div>
        {incomplete && <p className="text-xs text-warn">Complete every added class, relationship endpoint and property before saving.</p>}
        {save.isError && <ErrorBox error={save.error} />}
      </section>}

      {notice && (
        <div className="card flex items-center justify-between border-good/40 bg-good/5 text-sm">
          <span className="inline-flex items-center gap-2 text-good">
            <CheckCircle2 size={14} /> {notice}
          </span>
          <button className="btn-primary text-xs" onClick={onApproved}>
            Go to Versions
          </button>
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title={statusFilter === "proposed" ? "No change proposals pending." : `No ${statusFilter} proposals.`}
          hint="Governed agent/Studio ontology proposals land here for human review before they become a draft."
        />
      ) : (
        rows.map((p) => {
          const busy = (m: { isPending: boolean; variables?: string }) =>
            m.isPending && m.variables === p.id;
          const ents = p.changes?.add_entity_types ?? [];
          const rels = p.changes?.add_relation_types ?? [];
          const props = p.changes?.add_properties ?? [];
          const discovery = p.changes?.discovery_claim;
          return (
            <div key={p.id} className="card">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <GitPullRequestArrow size={15} className="shrink-0 text-accent" />
                    <span className="truncate font-medium">{p.title}</span>
                    <StatusPill status={p.status} />
                  </div>
                  <div className="mt-0.5 text-xs text-muted">
                    proposed by <code className="text-xs">{p.proposed_by || "system"}</code>
                    {p.created_at && <> · {new Date(p.created_at).toLocaleString()}</>}
                  </div>
                  {p.rationale && <p className="mt-2 text-sm text-muted">{p.rationale}</p>}
                </div>
                {discovery ? <Link to="/catalog" search={{ tab: "review" }} className="btn shrink-0 text-xs">Review in Catalog</Link> : p.status === "proposed" && <div className="flex shrink-0 flex-wrap gap-1">
                  <button className="btn text-xs" onClick={() => openEditor(p)}><Pencil size={13} /> Edit</button>
                  {canAdmin && <button className="btn text-xs text-bad" disabled={remove.isPending} onClick={() => { if (window.confirm(`Delete proposal ${p.title}?`)) remove.mutate(p.id); }}><Trash2 size={13} /> Delete</button>}
                  {canAdmin && <>
                  <button
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs text-good hover:bg-border/40 disabled:opacity-50"
                    title="Approve → create draft"
                    disabled={busy(approve) || busy(reject)}
                    onClick={() => approve.mutate(p.id)}
                  >
                    {busy(approve) ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Approve
                  </button>
                  <button
                    className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1.5 text-xs text-bad hover:bg-border/40 disabled:opacity-50"
                    title="Reject proposal"
                    disabled={busy(approve) || busy(reject)}
                    onClick={() => reject.mutate(p.id)}
                  >
                    {busy(reject) ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                    Reject
                  </button>
                  </>}
                </div>}
              </div>

              {discovery ? <div className="mt-3 border border-border bg-bg/40 p-3 text-sm">
                <div className="font-medium">{discovery.claim?.name || "Business term"} <span className="text-xs text-muted">· {discovery.kind || "discovery"}{discovery.claim?.data_entity ? ` · ${discovery.claim.data_entity}` : ""}</span></div>
                {discovery.claim?.definition && <p className="mt-1 text-muted">{discovery.claim.definition}</p>}
                <p className="mt-2 text-xs text-muted">Review this term and its evidence in Catalog, then select approved terms into a draft. This claim does not add a new class by itself.</p>
              </div> : <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <ChangeColumn label="New entity types" count={ents.length}>
                  {ents.map((e) => (
                    <li key={e.name} className="truncate" title={e.description}>
                      <code className="text-xs">{e.name}</code>
                      {e.parent && <span className="text-muted"> ◂ {e.parent}</span>}
                    </li>
                  ))}
                </ChangeColumn>
                <ChangeColumn label="New relation types" count={rels.length}>
                  {rels.map((r) => (
                    <li key={r.name} className="truncate">
                      <code className="text-xs">{r.name}</code>
                      <span className="text-muted"> ({r.source}→{r.target})</span>
                    </li>
                  ))}
                </ChangeColumn>
                <ChangeColumn label="New properties" count={props.length}>
                  {props.map((pr, i) => (
                    <li key={`${pr.entity}.${pr.property}.${i}`} className="truncate">
                      <code className="text-xs">{pr.entity}.{pr.property}</code>
                    </li>
                  ))}
                </ChangeColumn>
              </div>}
            </div>
          );
        })
      )}
      {(approve.isError || reject.isError || remove.isError) && <ErrorBox error={approve.error ?? reject.error ?? remove.error} />}
    </div>
  );
}

function ChangeColumn({
  label, count, children,
}: { label: string; count: number; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-bg/40 p-2">
      <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted">
        {label} <span className="text-fg">({count})</span>
      </div>
      {count === 0 ? (
        <div className="text-xs text-muted">—</div>
      ) : (
        <ul className="space-y-0.5 text-sm">{children}</ul>
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Versions tab                                                                //
// --------------------------------------------------------------------------- //

function VersionsTab({
  ws, onShowCard, onValidate, onReview,
}: { ws: string | null; onShowCard: (id: string) => void; onValidate: (id: string) => void; onReview: (id: string) => void }) {
  return (
    <PaginatedTable<OntologyVersion>
      endpoint="/ontology/versions/"
      baseParams={{ workspace: ws ?? undefined }}
      searchPlaceholder="Search by semver or status…"
      statusOptions={[
        { label: "Draft",     value: "draft"     },
        { label: "Published", value: "published" },
        { label: "Archived",  value: "archived"  },
      ]}
      cols={[
        { header: "Semver", cell: (v) => <code className="font-mono text-xs">{v.semver}</code> },
        { header: "Status", cell: (v) => <StatusPill status={v.status} /> },
        { header: "ID",     cell: (v) => (
            <code className="font-mono text-[11px] text-muted">{v.id.slice(0, 8)}</code>
          ),
        },
        { header: "Actions", cell: (v) => <VersionActions version={v} ws={ws} onShowCard={onShowCard} onValidate={onValidate} onReview={onReview} /> },
      ]}
      empty="No ontology versions yet. Use 'Generate from domain' or 'Start from standard' →"
    />
  );
}

import { createPortal } from "react-dom";
// Make sure useState, useRef, useEffect, useLayoutEffect are imported from "react"

function VersionActions({
  version, ws, onShowCard, onValidate, onReview,
}: { version: OntologyVersion; ws: string | null; onShowCard: (id: string) => void; onValidate: (id: string) => void; onReview: (id: string) => void }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [rebaseOpen, setRebaseOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const exportBtnRef = useRef<HTMLButtonElement>(null);
  const exportPanelRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  // Position the portaled panel against the trigger button whenever it opens
  useLayoutEffect(() => {
    if (!open || !exportBtnRef.current) return;

    const place = () => {
      const rect = exportBtnRef.current!.getBoundingClientRect();
      const panelWidth = 256; // matches w-64
      const margin = 12;
      setMenuPos({
        top: rect.bottom + 8,
        left: Math.min(
          Math.max(rect.right - panelWidth, margin),
          window.innerWidth - panelWidth - margin,
        ),
        width: panelWidth,
      });
    };

    place();
    // Keep it anchored if the layout shifts (fonts loading, etc.)
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);

  // Close on outside click / Escape / scroll (since the panel is no longer
  // a DOM descendant of the trigger, we check both refs explicitly)
  useEffect(() => {
    if (!open) return;

    const dismiss = (event: MouseEvent) => {
      const target = event.target as Node;
      const clickedButton = exportBtnRef.current?.contains(target);
      const clickedPanel = exportPanelRef.current?.contains(target);
      if (!clickedButton && !clickedPanel) setOpen(false);
    };
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const dismissOnScroll = (event: Event) => {
      // Ignore scroll events that originate inside the panel itself
      // (e.g. scrolling a long export-formats list)
      if (exportPanelRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };

    document.addEventListener("mousedown", dismiss);
    document.addEventListener("keydown", dismissOnEscape);
    window.addEventListener("scroll", dismissOnScroll, true);
    return () => {
      document.removeEventListener("mousedown", dismiss);
      document.removeEventListener("keydown", dismissOnEscape);
      window.removeEventListener("scroll", dismissOnScroll, true);
    };
  }, [open]);

  const completeReview = useMutation({
    mutationFn: (notes: string) => rawFetch<OntologyVersion>(
      `/ontology/versions/${version.id}/complete-review/`,
      { method: "POST", body: JSON.stringify({ acknowledged: true, notes }) },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] }),
  });

  const rollback = useMutation({
    mutationFn: () => rawFetch<OntologyVersion>(
      `/ontology/versions/${version.id}/rollback/`,
      { method: "POST", body: "{}" },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
      qc.invalidateQueries({ queryKey: ["experts-diagnostics"] });
    },
  });

  async function handleExport(fmt: string, ext: string) {
    setErr(null);
    setOpen(false);
    try {
      await downloadBlob(
        `/ontology/versions/${version.id}/export/?fmt=${fmt}`,
        `ontology-${version.semver}.${ext}`,
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="btn inline-flex items-center gap-1 text-xs"
        onClick={() => onShowCard(version.id)}
        title="Open ontology card (one-page docs + mini-graph)"
      >
        <BookOpen size={12} /> Card
      </button>
      <button
        type="button"
        className="btn inline-flex items-center gap-1 text-xs"
        onClick={() => onValidate(version.id)}
        title="Release readiness, SHACL, competency questions and reasoner"
      >
        <ShieldCheck size={12} /> Readiness
      </button>
      {version.status === "draft" && (
        <button
          type="button"
          className="btn inline-flex items-center gap-1 text-xs"
          onClick={() => setAddOpen(true)}
          title="Review and edit classes, relationships, and data properties in this draft"
        >
          <Pencil size={12} /> Model
        </button>
      )}
      {version.status === "draft" && version.review_required && !version.review_completed_at && (
        <button
          type="button"
          className="btn inline-flex items-center gap-1 border-good/40 text-xs text-good"
          disabled={completeReview.isPending}
          title={`${version.review_draft_nodes?.length ?? 0} authored node(s) require named SME acceptance`}
          onClick={() => {
            const notes = window.prompt(
              `Complete named SME review for ${version.semver}?\n\nConfirm that the ontology content and ${version.review_draft_nodes?.length ?? 0} authored review item(s) have been inspected. Add review notes:`,
              "Reviewed in Ontology; terminology and constraints accepted.",
            );
            if (notes !== null) completeReview.mutate(notes);
          }}
        >
          <BadgeCheck size={12} />
          {completeReview.isPending ? "Recording…" : "Complete SME review"}
        </button>
      )}
      {version.review_required && version.review_completed_at && (
        <span className="pill border-good/30 text-good" title={version.review_notes || "Named SME review completed"}>
          Reviewed by {version.review_completed_by}
        </span>
      )}
      {version.status === "draft" && version.review_required && version.structured_review_enabled && (
        <button
          type="button"
          className="btn inline-flex items-center gap-1 border-accent/40 text-xs text-accent"
          onClick={() => onReview(version.id)}
          title="Record role assignments, item dispositions, conditions, and change requests"
        >
          <ListChecks size={12} /> Structured review
        </button>
      )}
      <button
        type="button"
        className="btn inline-flex items-center gap-1 text-xs"
        onClick={() => setRebaseOpen(true)}
        title="Rebase customer changes from an old base onto a newer base"
      >
        <GitBranch size={12} /> Rebase
      </button>
      {version.status === "draft" && (
        <button
          type="button"
          className="btn inline-flex items-center gap-1 text-xs"
          onClick={() => setLabelsOpen(true)}
          title="Edit multilingual labels without changing semantic IRIs"
        >
          <Languages size={12} /> Labels
        </button>
      )}
      {version.status === "archived" && (
        <button
          type="button"
          className="btn inline-flex items-center gap-1 text-xs"
          onClick={() => {
            if (window.confirm(`Re-publish ${version.semver}?`)) rollback.mutate();
          }}
          disabled={rollback.isPending}
          title="Restore this archived version as the active schema"
        >
          <RotateCcw size={12} />
          {rollback.isPending ? "…" : "Restore"}
        </button>
      )}

      <button
        ref={exportBtnRef}
        type="button"
        className="btn inline-flex items-center gap-1.5 text-xs"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Download size={12} /> Export
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && menuPos && createPortal(
        <div
          ref={exportPanelRef}
          className="fixed z-[9999] max-h-[calc(100vh-6rem)] w-64 overflow-y-auto rounded-md border border-border bg-card shadow-2xl ring-1 ring-black/5"
          style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}
          role="menu"
          aria-label={`Export ontology ${version.semver}`}
        >
          <div className="border-b border-border bg-bg/50 px-3 py-2.5">
            <div className="text-xs font-semibold text-fg">Export ontology</div>
            <div className="mt-0.5 font-mono text-[10px] text-muted">Version {version.semver}</div>
          </div>
          <div className="p-1.5">
            {EXPORT_FORMATS.map((f) => (
              <button
                key={f.fmt}
                type="button"
                role="menuitem"
                className="group flex w-full items-center gap-2.5 rounded px-2 py-2 text-left hover:bg-accent/10 focus-visible:bg-accent/10 focus-visible:outline-none"
                onClick={() => handleExport(f.fmt, f.ext)}
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded border border-border bg-bg/60 text-muted group-hover:border-accent/40 group-hover:text-accent">
                  <FileCode2 size={13} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-medium text-fg">{f.label}</span>
                  <span className="block truncate text-[10px] text-muted">{f.description}</span>
                </span>
                <span className="rounded border border-border px-1.5 py-0.5 font-mono text-[9px] uppercase text-muted">.{f.ext}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}

      {err && <span className="max-w-[24rem] truncate text-[11px] text-bad" title={err}>{err}</span>}
      {completeReview.isError && (
        <span className="max-w-[24rem] truncate text-[11px] text-bad" title={completeReview.error instanceof Error ? completeReview.error.message : String(completeReview.error)}>
          {completeReview.error instanceof Error ? completeReview.error.message : String(completeReview.error)}
        </span>
      )}
      {rollback.isError && (
        <span
          className="max-w-[24rem] truncate text-[11px] text-bad"
          title={rollback.error instanceof Error ? rollback.error.message : String(rollback.error)}
        >
          {rollback.error instanceof Error ? rollback.error.message : String(rollback.error)}
        </span>
      )}
      {addOpen && (
        <AddConceptDrawer
          versionId={version.id}
          semver={version.semver}
          ws={ws}
          onClose={() => setAddOpen(false)}
        />
      )}
      {labelsOpen && (
        <LabelMatrixDrawer
          versionId={version.id}
          semver={version.semver}
          ws={ws}
          onClose={() => setLabelsOpen(false)}
        />
      )}
      {rebaseOpen && (
        <RebaseDrawer
          customerVersion={version}
          ws={ws}
          onClose={() => setRebaseOpen(false)}
        />
      )}
    </div>
  );
}

const REVIEW_ROLE_OPTIONS = [
  ["semantic_architect", "Semantic architect"],
  ["domain_sme", "Domain SME"],
  ["compliance_legal", "Compliance / legal"],
  ["data_governance", "Data governance"],
  ["licensing_owner", "Licensing owner"],
  ["implementation_owner", "Implementation owner"],
] as const;

const REVIEW_ITEM_CATEGORY_OPTIONS = [
  ["class_definition", "Class definition"],
  ["property_definition", "Property definition"],
  ["external_standard", "External standard"],
  ["mapping_strength", "Mapping strength"],
  ["competency_question", "Competency question"],
  ["validation_rule", "Validation rule"],
  ["release_gate", "Release gate"],
  ["other", "Other"],
] as const;

function StructuredReviewDrawer({
  versionId, onClose,
}: { versionId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [role, setRole] = useState<string>("domain_sme");
  const [assignedTo, setAssignedTo] = useState("");
  const [assignmentRequired, setAssignmentRequired] = useState(true);
  const [category, setCategory] = useState<string>("class_definition");
  const [subjectIri, setSubjectIri] = useState("");
  const [summary, setSummary] = useState("");
  const [itemRequired, setItemRequired] = useState(true);

  const versionQ = useQuery<OntologyVersion>({
    queryKey: ["ontology-version", versionId, "structured-review"],
    queryFn: () => rawFetch(`/ontology/versions/${versionId}/`),
  });
  const reviewQ = useQuery<StructuredReviewResponse>({
    queryKey: ["ontology-structured-review", versionId],
    queryFn: () => rawFetch(`/ontology/versions/${versionId}/structured-review/`),
  });

  const createAssignment = useMutation({
    mutationFn: () => rawFetch(`/ontology/review-assignments/`, {
      method: "POST",
      body: JSON.stringify({
        workspace: versionQ.data?.workspace,
        version: versionId,
        role,
        assigned_to: assignedTo,
        required: assignmentRequired,
      }),
    }),
    onSuccess: () => {
      setAssignedTo("");
      qc.invalidateQueries({ queryKey: ["ontology-structured-review", versionId] });
    },
  });

  const createItem = useMutation({
    mutationFn: () => rawFetch(`/ontology/review-items/`, {
      method: "POST",
      body: JSON.stringify({
        workspace: versionQ.data?.workspace,
        version: versionId,
        category,
        subject_iri: subjectIri,
        summary,
        required: itemRequired,
      }),
    }),
    onSuccess: () => {
      setSubjectIri("");
      setSummary("");
      qc.invalidateQueries({ queryKey: ["ontology-structured-review", versionId] });
    },
  });

  const assignmentTransition = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => rawFetch(
      `/ontology/review-assignments/${id}/transition/`,
      { method: "POST", body: JSON.stringify(body) },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ontology-structured-review", versionId] }),
  });

  const itemTransition = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => rawFetch(
      `/ontology/review-items/${id}/transition/`,
      { method: "POST", body: JSON.stringify(body) },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ontology-structured-review", versionId] }),
  });

  const blockers = reviewQ.data?.release_policy?.checks?.structured_review?.blockers as Record<string, string[]> | undefined;

  const handleAssignmentDecision = (id: string, decision: "approve" | "approve_with_conditions" | "defer" | "reject") => {
    const rationale = window.prompt("Review rationale:", "") ?? "";
    if (decision === "approve_with_conditions") {
      const text = window.prompt("Approval condition:", "Attach the signed review evidence.");
      if (!text) return;
      assignmentTransition.mutate({ id, body: { decision, rationale, conditions: [{ text, satisfied: false }] } });
      return;
    }
    assignmentTransition.mutate({ id, body: { decision, rationale } });
  };

  const handleItemDecision = (item: StructuredReviewItem, disposition: "accept" | "modify" | "exclude" | "defer" | "escalate" | "reject" | "request_changes") => {
    const rationale = window.prompt("Disposition rationale:", item.rationale || "") ?? "";
    const body: Record<string, unknown> = { disposition, rationale };
    if (item.category === "mapping_strength" && ["accept", "modify", "exclude"].includes(disposition)) {
      const mappingStrength = window.prompt("Mapping strength (equivalence | subclass | skos):", item.mapping_strength || "subclass");
      if (!mappingStrength) return;
      body.mapping_strength = mappingStrength;
    }
    if (disposition === "request_changes") {
      const title = window.prompt("Child draft title:", item.summary || "Requested ontology change");
      if (!title) return;
      const changesText = window.prompt(
        "Change set JSON:",
        '{"add_entity_types":[{"name":"Project","description":"A body of work"}]}'
      );
      if (!changesText) return;
      try {
        body.title = title;
        body.changes = JSON.parse(changesText);
      } catch {
        window.alert("Change set must be valid JSON.");
        return;
      }
    }
    itemTransition.mutate({ id: item.id, body });
  };

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="flex h-full w-full max-w-[1040px] flex-col overflow-hidden border-l border-border bg-card/95 backdrop-blur-md shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted">Structured review</div>
            <div className="text-sm font-medium">{versionQ.data?.semver || versionId.slice(0, 8)}</div>
          </div>
          <button type="button" className="btn p-1" onClick={onClose} aria-label="Close structured review">
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {versionQ.isError && <ErrorBox error={versionQ.error} />}
          {reviewQ.isError && <ErrorBox error={reviewQ.error} />}

          {blockers && (
            <div className="card mb-4 border-bad/30 bg-bad/5 text-xs">
              <div className="font-semibold text-bad">Current publish blockers</div>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {Object.entries(blockers).filter(([, values]) => Array.isArray(values) && values.length > 0).map(([key, values]) => (
                  <div key={key} className="rounded border border-border bg-bg/60 px-2 py-2">
                    <div className="uppercase text-muted">{key.replaceAll("_", " ")}</div>
                    <div className="mt-1 space-y-1">
                      {values.map((value) => <div key={value} className="font-mono text-[11px] text-fg">{value}</div>)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="space-y-3">
              <div className="card space-y-2">
                <div className="font-semibold text-fg">Role assignments</div>
                <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                  <select className="input" value={role} onChange={(event) => setRole(event.target.value)}>
                    {REVIEW_ROLE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <input className="input" value={assignedTo} onChange={(event) => setAssignedTo(event.target.value)} placeholder="Assigned reviewer" />
                </div>
                <label className="flex items-center gap-2 text-xs text-muted">
                  <input type="checkbox" checked={assignmentRequired} onChange={(event) => setAssignmentRequired(event.target.checked)} />
                  Required for publication
                </label>
                <button className="btn-primary text-xs" disabled={!versionQ.data?.workspace || createAssignment.isPending} onClick={() => createAssignment.mutate()}>
                  {createAssignment.isPending ? "Saving…" : "Add assignment"}
                </button>
                {createAssignment.isError && <ErrorBox error={createAssignment.error} />}
              </div>

              <div className="space-y-2">
                {(reviewQ.data?.assignments ?? []).map((assignment) => (
                  <div key={assignment.id} className="card text-xs">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="font-semibold text-fg">{assignment.role.replaceAll("_", " ")}</div>
                        <div className="text-muted">{assignment.assigned_to || "Unassigned"}</div>
                      </div>
                      <StatusPill status={assignment.status || "pending"} />
                    </div>
                    {assignment.conditions?.length > 0 && (
                      <div className="mt-2 space-y-1 text-muted">
                        {assignment.conditions.map((condition, index) => (
                          <div key={`${assignment.id}-${index}`}>{condition.text || "Condition"}{condition.satisfied ? " (done)" : " (open)"}</div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button className="btn text-xs" onClick={() => handleAssignmentDecision(assignment.id, "approve")}>Approve</button>
                      <button className="btn text-xs" onClick={() => handleAssignmentDecision(assignment.id, "approve_with_conditions")}>Approve with conditions</button>
                      <button className="btn text-xs" onClick={() => handleAssignmentDecision(assignment.id, "defer")}>Defer</button>
                      <button className="btn text-xs text-bad" onClick={() => handleAssignmentDecision(assignment.id, "reject")}>Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="card space-y-2">
                <div className="font-semibold text-fg">Review items</div>
                <select className="input" value={category} onChange={(event) => setCategory(event.target.value)}>
                  {REVIEW_ITEM_CATEGORY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <input className="input" value={subjectIri} onChange={(event) => setSubjectIri(event.target.value)} placeholder="Subject IRI or artifact id" />
                <textarea className="input min-h-20" value={summary} onChange={(event) => setSummary(event.target.value)} placeholder="What requires review?" />
                <label className="flex items-center gap-2 text-xs text-muted">
                  <input type="checkbox" checked={itemRequired} onChange={(event) => setItemRequired(event.target.checked)} />
                  Required for publication
                </label>
                <button className="btn-primary text-xs" disabled={!versionQ.data?.workspace || createItem.isPending || !summary.trim()} onClick={() => createItem.mutate()}>
                  {createItem.isPending ? "Saving…" : "Add review item"}
                </button>
                {createItem.isError && <ErrorBox error={createItem.error} />}
              </div>

              <div className="space-y-2">
                {(reviewQ.data?.items ?? []).map((item) => (
                  <div key={item.id} className="card text-xs">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <div className="font-semibold text-fg">{item.summary || item.category}</div>
                        <div className="font-mono text-[11px] text-muted">{item.subject_iri || item.category}</div>
                      </div>
                      <StatusPill status={item.disposition || "pending"} />
                    </div>
                    {item.mapping_strength && <div className="mt-1 text-muted">Strength: {item.mapping_strength}</div>}
                    {item.child_version && <div className="mt-1 text-good">Child draft: {item.child_version}</div>}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button className="btn text-xs" onClick={() => handleItemDecision(item, "accept")}>Accept</button>
                      <button className="btn text-xs" onClick={() => handleItemDecision(item, "modify")}>Modify</button>
                      <button className="btn text-xs" onClick={() => handleItemDecision(item, "exclude")}>Exclude</button>
                      <button className="btn text-xs" onClick={() => handleItemDecision(item, "defer")}>Defer</button>
                      <button className="btn text-xs" onClick={() => handleItemDecision(item, "escalate")}>Escalate</button>
                      <button className="btn text-xs" onClick={() => handleItemDecision(item, "request_changes")}>Request changes</button>
                      <button className="btn text-xs text-bad" onClick={() => handleItemDecision(item, "reject")}>Reject</button>
                    </div>
                  </div>
                ))}
              </div>
              {itemTransition.isError && <ErrorBox error={itemTransition.error} />}
              {assignmentTransition.isError && <ErrorBox error={assignmentTransition.error} />}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

interface RebaseConflict {
  code: string;
  subject: string;
  predicate: string;
  remediation: string;
}

interface RebaseResponse {
  id: string;
  status: "conflicts" | "succeeded";
  result_version_id: string | null;
  report: {
    mode: string;
    conflict_count: number;
    conflicts: RebaseConflict[];
    warning: string;
  };
}

function RebaseDrawer({
  customerVersion, ws, onClose,
}: { customerVersion: OntologyVersion; ws: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [oldBase, setOldBase] = useState("");
  const [newBase, setNewBase] = useState("");
  const [resultSemver, setResultSemver] = useState("");

  const versions = useQuery({
    queryKey: ["ontology-rebase-versions", ws],
    queryFn: async () => {
      const response = await rawFetch<{ results?: OntologyVersion[] } | OntologyVersion[]>(
        "/ontology/versions/?page_size=500",
      );
      return Array.isArray(response) ? response : (response.results ?? []);
    },
    enabled: Boolean(ws),
  });
  const rebase = useMutation({
    mutationFn: () => rawFetch<RebaseResponse>(
      `/ontology/versions/${customerVersion.id}/rebase/`,
      {
        method: "POST",
        body: JSON.stringify({
          old_base_version_id: oldBase,
          new_base_version_id: newBase,
          result_semver: resultSemver.trim(),
        }),
      },
    ),
    onSuccess: (result) => {
      if (result.result_version_id) {
        qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
      }
    },
  });
  const choices = (versions.data ?? []).filter((item) => item.id !== customerVersion.id);

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="flex h-full w-full max-w-[520px] flex-col overflow-hidden border-l border-border bg-card/95 shadow-2xl backdrop-blur-md">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="text-xs uppercase text-muted">Rebase customer extension</div>
            <div className="font-mono text-xs">{customerVersion.semver}</div>
          </div>
          <button type="button" className="btn p-1" onClick={onClose} aria-label="Close"><X size={14} /></button>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          <div className="rounded border border-warn/40 bg-warn/5 px-3 py-2 text-xs text-warn">
            This is a projected semantic rebase. Canonical-only OWL constructs must be reviewed in a full RDF editor. Conflicts never create a partial draft.
          </div>
          <label className="block">
            <div className="mb-1 text-xs uppercase text-muted">Old base</div>
            <select className="input text-sm" value={oldBase} onChange={(event) => setOldBase(event.target.value)}>
              <option value="">Select the base originally extended</option>
              {choices.map((item) => <option key={item.id} value={item.id}>{item.semver} · {item.status}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="mb-1 text-xs uppercase text-muted">New base</div>
            <select className="input text-sm" value={newBase} onChange={(event) => setNewBase(event.target.value)}>
              <option value="">Select the updated base</option>
              {choices.map((item) => <option key={item.id} value={item.id}>{item.semver} · {item.status}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="mb-1 text-xs uppercase text-muted">Result version</div>
            <input className="input text-sm" placeholder="e.g. 2.0.1-customer" value={resultSemver} onChange={(event) => setResultSemver(event.target.value)} />
          </label>
          {versions.isError && <ErrorBox error={versions.error} />}
          {rebase.isError && <ErrorBox error={rebase.error} />}
          {rebase.data?.status === "succeeded" && (
            <div className="rounded border border-good/40 bg-good/5 px-3 py-2 text-xs text-good">
              Rebase completed. Draft <code className="font-mono">{resultSemver}</code> is ready for validation.
            </div>
          )}
          {rebase.data?.status === "conflicts" && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-bad">{rebase.data.report.conflict_count} conflict(s) require resolution</div>
              {rebase.data.report.conflicts.map((conflict, index) => (
                <div key={`${conflict.code}-${index}`} className="rounded border border-bad/40 bg-bad/5 px-3 py-2 text-xs">
                  <div className="font-mono text-bad">{conflict.code}</div>
                  <div className="mt-1 break-all text-muted">{conflict.subject}</div>
                  <div className="mt-1">{conflict.remediation}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1 text-sm"
            disabled={!oldBase || !newBase || !resultSemver.trim() || rebase.isPending}
            onClick={() => rebase.mutate()}
          >
            <GitBranch size={14} /> {rebase.isPending ? "Comparing…" : "Run rebase"}
          </button>
          <button type="button" className="btn text-sm" onClick={onClose}>Close</button>
        </div>
      </aside>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Add-concept drawer — append a single entity or relationship to a DRAFT       //
// version without re-generating or re-importing. The fast "I just need one     //
// more class" path for both novices and specialists.                          //
// --------------------------------------------------------------------------- //

interface EntityRow {
  id: string;
  name: string;
  properties_digest: string;
  semantic_iri?: string;
  source_iri?: string;
  parent?: string | null;
  description?: string;
  properties?: { type?: string; properties?: Record<string, { type?: string; description?: string }> };
}

interface RelationshipRow {
  id: string;
  name: string;
  source_type: string;
  target_type: string;
  cardinality: string;
  description?: string;
}

interface ModelPreview {
  changes: {
    relationships: Array<{ name: string; source: string; target: string; description: string }>;
    properties: Array<{ entity: string; property: string; schema: { type: string; description: string } }>;
  };
  message: string;
}

function AddConceptDrawer({
  versionId, semver, ws, onClose,
}: { versionId: string; semver: string; ws: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<"entity" | "relation" | "property">("entity");
  const [editingId, setEditingId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [parent, setParent] = useState("");
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [cardinality, setCardinality] = useState("N-N");
  const [dataType, setDataType] = useState("string");
  const [done, setDone] = useState<string | null>(null);
  const [domain, setDomain] = useState("general");
  const [selectedSuggestions, setSelectedSuggestions] = useState<string[]>([]);

  const aiConfig = useQuery<{ enabled: boolean; credential_ids: string[] }>({
    queryKey: ["catalog-ai-credentials", ws],
    queryFn: () => rawFetch("/iam/credentials/catalog-ai/"),
    enabled: !!ws,
  });
  const suggestions = useMutation<ModelPreview>({
    mutationFn: () => rawFetch(`/ontology/versions/${versionId}/suggest-model/`, {
      method: "POST", body: JSON.stringify({ domain: domain.trim().toLowerCase() }),
    }),
    onSuccess: (preview) => setSelectedSuggestions([
      ...preview.changes.relationships.map((_, index) => `relation:${index}`),
      ...preview.changes.properties.map((_, index) => `property:${index}`),
    ]),
  });
  const stageSuggestions = useMutation({
    mutationFn: () => {
      const preview = suggestions.data;
      if (!preview || !ws) throw new Error("Request suggestions first.");
      const add_relation_types = preview.changes.relationships.filter((_, index) => selectedSuggestions.includes(`relation:${index}`));
      const add_properties = preview.changes.properties.filter((_, index) => selectedSuggestions.includes(`property:${index}`));
      if (!add_relation_types.length && !add_properties.length) throw new Error("Select at least one suggestion.");
      return rawFetch("/ontology/change-proposals/", { method: "POST", body: JSON.stringify({
        workspace: ws, base_version: versionId, title: `Review ${domain} model suggestions`,
        rationale: "Workspace-key AI preview. SME review required; no changes applied to this draft.",
        changes: { add_relation_types, add_properties },
      }) });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["change-proposals", ws] }),
  });

  const entities = useQuery({
    queryKey: ["ontology-entities", versionId],
    queryFn: async () => {
      const r = await rawFetch<{ results?: EntityRow[] } | EntityRow[]>(
        `/ontology/entities/?version=${versionId}&page_size=500`,
      );
      return Array.isArray(r) ? r : (r.results ?? []);
    },
  });

  const relations = useQuery({
    queryKey: ["ontology-relations", versionId],
    queryFn: async () => {
      const result = await rawFetch<{ results?: RelationshipRow[] } | RelationshipRow[]>(
        `/ontology/relations/?version=${versionId}&page_size=500`,
      );
      return Array.isArray(result) ? result : (result.results ?? []);
    },
  });

  const resetForm = () => {
    setEditingId(""); setName(""); setDescription(""); setParent("");
    setSource(""); setTarget(""); setCardinality("N-N"); setDataType("string");
  };
  const invalidateModel = () => {
    qc.invalidateQueries({ queryKey: ["ontology-entities", versionId] });
    qc.invalidateQueries({ queryKey: ["ontology-relations", versionId] });
    qc.invalidateQueries({ queryKey: ["ontology-graph", versionId] });
    qc.invalidateQueries({ queryKey: ["ontology-card", versionId] });
  };

  const create = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim();
      if (!trimmed) throw new Error("Name is required.");
      if (kind === "entity") {
        const body: Record<string, unknown> = { version: versionId, workspace: ws, name: trimmed, description };
        if (editingId || parent) body.parent = parent || null;
        return rawFetch<EntityRow>(editingId ? `/ontology/entities/${editingId}/` : "/ontology/entities/", {
          method: editingId ? "PATCH" : "POST", body: JSON.stringify(body),
        });
      }
      if (kind === "property") {
        const entity = (entities.data ?? []).find((row) => row.id === source);
        if (!entity) throw new Error("Choose a class for this data property.");
        return rawFetch<EntityRow>(`/ontology/entities/${entity.id}/data-properties/`, {
          method: "POST", body: JSON.stringify({
            operation: editingId ? "update" : "add", name: editingId || trimmed,
            new_name: editingId ? trimmed : "",
            schema: { type: dataType, description: description.trim() },
            expected_properties_digest: entity.properties_digest,
          }),
        });
      }
      if (!source || !target) throw new Error("Source and target entities are required.");
      return rawFetch(editingId ? `/ontology/relations/${editingId}/` : "/ontology/relations/", {
        method: editingId ? "PATCH" : "POST",
        body: JSON.stringify({
          version: versionId, workspace: ws, name: trimmed,
          source_type: source, target_type: target, cardinality, description,
        }),
      });
    },
    onSuccess: () => {
      setDone(`${kind === "entity" ? "Entity" : "Relationship"} “${name.trim()}” added.`);
      if (kind === "property") setDone(`Data property ${name.trim()} saved.`);
      resetForm();
      invalidateModel();
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      if (kind === "property") {
        const entity = (entities.data ?? []).find((row) => row.id === source);
        if (!entity || !editingId) throw new Error("Select a data property first.");
        return rawFetch(`/ontology/entities/${entity.id}/data-properties/`, { method: "POST",
          body: JSON.stringify({ operation: "delete", name: editingId,
            expected_properties_digest: entity.properties_digest }) });
      }
      return rawFetch(kind === "entity" ? `/ontology/entities/${editingId}/` : `/ontology/relations/${editingId}/`, { method: "DELETE" });
    },
    onSuccess: () => { resetForm(); invalidateModel(); },
  });

  const ents = entities.data ?? [];
  const rels = relations.data ?? [];
  const propertyRows = ents.flatMap((entity) => Object.entries(entity.properties?.properties ?? {})
    .map(([property, schema]) => ({ entity, property, schema })));

  const selectEntity = (entity: EntityRow) => {
    setEditingId(entity.id); setName(entity.name); setDescription(entity.description ?? "");
    setParent(entity.parent ?? ""); setDone(null);
  };
  const selectRelation = (relation: RelationshipRow) => {
    setEditingId(relation.id); setName(relation.name); setDescription(relation.description ?? "");
    setSource(relation.source_type); setTarget(relation.target_type);
    setCardinality(relation.cardinality); setDone(null);
  };
  const selectProperty = (entity: EntityRow, property: string, schema: { type?: string; description?: string }) => {
    setEditingId(property); setName(property); setSource(entity.id);
    setDataType(schema.type ?? "string"); setDescription(schema.description ?? ""); setDone(null);
  };

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="flex h-full w-full max-w-[620px] flex-col overflow-hidden border-l border-border bg-card/95 shadow-2xl backdrop-blur-md">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted">Draft model</div>
            <div className="text-sm font-medium">
              <code className="font-mono text-xs">{semver}</code>
              <code className="ml-2 font-mono text-[11px] text-muted">{versionId.slice(0, 8)}</code>
            </div>
          </div>
          <button type="button" className="btn p-1" onClick={onClose} aria-label="Close"><X size={14} /></button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {!ws && (
            <div className="rounded border border-warn/40 bg-warn/5 px-3 py-2 text-xs text-warn">
              Pick a workspace from the header before adding concepts.
            </div>
          )}

          <details className="border border-border bg-bg/40 p-3">
            <summary className="cursor-pointer text-sm font-semibold">Optional AI model suggestions</summary>
            <p className="mt-2 text-xs text-muted">Preview possible relationships and data properties from this draft's reviewed class names and a domain code. Safe descriptions go only to your selected workspace provider. Suggestions are not facts or applied changes; an SME must review a staged proposal.</p>
            <label className="mt-3 block text-xs text-muted">Domain code<input className="input mt-1 text-xs" value={domain} onChange={(event) => setDomain(event.target.value)} placeholder="insurance" /></label>
            {aiConfig.data?.enabled && aiConfig.data.credential_ids.length > 0 ?
              <button className="btn mt-2 text-xs" disabled={suggestions.isPending || !/^[a-z][a-z0-9_-]{1,39}$/.test(domain.trim()) || !ents.length} onClick={() => suggestions.mutate()}><Sparkles size={13} /> {suggestions.isPending ? "Suggesting…" : "Suggest with my LLM key"}</button> :
              <Link to="/settings" className="mt-2 inline-block text-xs text-accent underline">Add your LLM key to enable this</Link>}
            {!ents.length && <p className="mt-2 text-xs text-muted">Add a class before requesting suggestions.</p>}
            {suggestions.isError && <ErrorBox error={suggestions.error} />}
            {suggestions.data && <div className="mt-3 space-y-2 text-xs">
              <p>{suggestions.data.message}</p>
              {suggestions.data.changes.relationships.map((row, index) => <label key={`relation:${index}`} className="flex items-start gap-2 border border-border p-2"><input type="checkbox" checked={selectedSuggestions.includes(`relation:${index}`)} onChange={(event) => setSelectedSuggestions((current) => event.target.checked ? [...current, `relation:${index}`] : current.filter((key) => key !== `relation:${index}`))} /><span>Relationship: <strong>{row.source} → {row.name} → {row.target}</strong>{row.description && ` · ${row.description}`}</span></label>)}
              {suggestions.data.changes.properties.map((row, index) => <label key={`property:${index}`} className="flex items-start gap-2 border border-border p-2"><input type="checkbox" checked={selectedSuggestions.includes(`property:${index}`)} onChange={(event) => setSelectedSuggestions((current) => event.target.checked ? [...current, `property:${index}`] : current.filter((key) => key !== `property:${index}`))} /><span>Data property: <strong>{row.entity}.{row.property}</strong> · {row.schema.type}</span></label>)}
              <button className="btn-primary text-xs" disabled={stageSuggestions.isPending || stageSuggestions.isSuccess || !selectedSuggestions.length} onClick={() => stageSuggestions.mutate()}>Stage selected for review</button>
              {stageSuggestions.isSuccess && <p role="status" className="text-good">Staged in Change proposals. An admin must review it before a new draft is created.</p>}
              {stageSuggestions.isError && <ErrorBox error={stageSuggestions.error} />}
            </div>}
          </details>

          {/* Kind toggle */}
          <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
            {(["entity", "relation", "property"] as const).map((k) => (
              <button
                key={k}
                type="button"
                className={"rounded px-3 py-1 " + (kind === k ? "bg-accent/60 text-fg" : "text-muted hover:text-fg")}
                onClick={() => { setKind(k); resetForm(); setDone(null); }}
              >
                {k === "entity" ? "Class" : k === "relation" ? "Relationship" : "Data property"}
              </button>
            ))}
          </div>

          <div className="border border-border bg-bg/40 p-3">
            <div className="mb-2 flex items-center justify-between"><span className="text-xs font-semibold">Existing {kind === "entity" ? `classes (${ents.length})` : kind === "relation" ? `relationships (${rels.length})` : `data properties (${propertyRows.length})`}</span><button className="btn text-xs" onClick={() => { resetForm(); setDone(null); }}>New</button></div>
            <div className="flex max-h-36 flex-wrap gap-1 overflow-y-auto">
              {kind === "entity" && ents.map((entity) => <button key={entity.id} className="btn text-xs" onClick={() => selectEntity(entity)}>{entity.name}</button>)}
              {kind === "relation" && rels.map((relation) => <button key={relation.id} className="btn text-xs" onClick={() => selectRelation(relation)}>{relation.name} · {ents.find((row) => row.id === relation.source_type)?.name ?? "?"} → {ents.find((row) => row.id === relation.target_type)?.name ?? "?"}</button>)}
              {kind === "property" && propertyRows.map(({ entity, property, schema }) => <button key={`${entity.id}:${property}`} className="btn text-xs" onClick={() => selectProperty(entity, property, schema)}>{entity.name}.{property}</button>)}
            </div>
            {(kind === "entity" ? ents.length : kind === "relation" ? rels.length : propertyRows.length) === 0 && <p className="text-xs text-muted">None yet. Add one below.</p>}
          </div>

          <label className="block">
            <div className="mb-1 text-xs uppercase text-muted">Name</div>
            <input
              className="input text-sm"
              placeholder={kind === "entity" ? "e.g. Policy" : kind === "relation" ? "e.g. COVERS" : "e.g. policyNumber"}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>

          {kind === "entity" ? (
            <label className="block">
              <div className="mb-1 text-xs uppercase text-muted">Parent (optional)</div>
              <select className="input text-sm" value={parent} onChange={(e) => setParent(e.target.value)}>
                <option value="">— no parent —</option>
                {ents.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
              </select>
            </label>
          ) : kind === "relation" ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <div className="mb-1 text-xs uppercase text-muted">Source</div>
                  <select className="input text-sm" value={source} onChange={(e) => setSource(e.target.value)}>
                    <option value="">— select —</option>
                    {ents.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
                  </select>
                </label>
                <label className="block">
                  <div className="mb-1 text-xs uppercase text-muted">Target</div>
                  <select className="input text-sm" value={target} onChange={(e) => setTarget(e.target.value)}>
                    <option value="">— select —</option>
                    {ents.map((en) => <option key={en.id} value={en.id}>{en.name}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <div className="mb-1 text-xs uppercase text-muted">Cardinality</div>
                <select className="input text-sm" value={cardinality} onChange={(e) => setCardinality(e.target.value)}>
                  <option value="1-1">1-1 (one-to-one)</option>
                  <option value="1-N">1-N (one-to-many)</option>
                  <option value="N-N">N-N (many-to-many)</option>
                </select>
              </label>
              {ents.length === 0 && (
                <div className="text-[11px] text-muted">
                  This draft has no entities yet — add a couple of entities first.
                </div>
              )}
            </>
          ) : <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs text-muted">Class<select className="input mt-1 text-sm" value={source} onChange={(event) => setSource(event.target.value)}><option value="">Select a class</option>{ents.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}</select></label>
            <label className="block text-xs text-muted">Value type<select className="input mt-1 text-sm" value={dataType} onChange={(event) => setDataType(event.target.value)}><option value="string">Text</option><option value="integer">Whole number</option><option value="number">Number</option><option value="boolean">True or false</option></select></label>
          </div>}

          <label className="block">
            <div className="mb-1 text-xs uppercase text-muted">Description (optional)</div>
            <textarea
              className="input min-h-[72px] text-sm"
              placeholder="What this concept means in your domain."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          {(create.isError || remove.isError) && <ErrorBox error={create.error ?? remove.error} />}
          {done && (
            <div className="rounded border border-good/40 bg-good/5 px-3 py-2 text-xs text-good">
              {done} It’s live in the draft — validate or publish when ready.
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-border px-5 py-3">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1 text-sm"
            disabled={!ws || create.isPending || !name.trim() || (kind === "relation" && (!source || !target)) || (kind === "property" && !source)}
            onClick={() => { setDone(null); create.mutate(); }}
          >
            <Plus size={14} />
            {create.isPending ? "Saving…" : editingId ? "Save changes" : `Add ${kind === "entity" ? "class" : kind === "relation" ? "relationship" : "data property"}`}
          </button>
          {editingId && <button type="button" className="btn text-sm text-bad" disabled={remove.isPending} onClick={() => { if (window.confirm(`Remove ${name} from this draft?`)) remove.mutate(); }}><Trash2 size={13} /> Delete</button>}
          <button type="button" className="btn text-sm" onClick={onClose}>Close</button>
        </div>
      </aside>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Label matrix — language-aware display labels for existing DRAFT subjects.   //
// Structural names and semantic IRIs remain stable.                            //
// --------------------------------------------------------------------------- //

interface LexicalRow {
  id: string;
  subject_iri: string;
  predicate: "label" | "pref_label" | "alt_label" | "hidden_label" | "comment" | "definition";
  language: string;
  value: string;
}

interface LabelSubject {
  kind: "Class" | "Relationship" | "Property" | "Scheme" | "Concept";
  name: string;
  semantic_iri: string;
}

function pageResults<T>(value: { results?: T[] } | T[]): T[] {
  return Array.isArray(value) ? value : (value.results ?? []);
}

function labelKey(subjectIri: string, language: string): string {
  return `${subjectIri}\u0000${language}`;
}

function primaryLexicalEntry(entries: LexicalRow[], subjectIri: string, language: string) {
  return entries.find((entry) =>
    entry.subject_iri === subjectIri && entry.language === language && entry.predicate === "pref_label"
  ) ?? entries.find((entry) =>
    entry.subject_iri === subjectIri && entry.language === language && entry.predicate === "label"
  );
}

interface LexicalImportReport {
  rows: number;
  unique: number;
  created: number;
  existing: number;
  duplicates_in_file: number;
  languages: string[];
  predicates: string[];
  dry_run: boolean;
}

function BulkLabelImport({ versionId, onImported }: { versionId: string; onImported: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<LexicalImportReport | null>(null);

  const upload = useMutation({
    mutationFn: async (dryRun: boolean) => {
      if (!file) throw new Error("Choose a CSV or JSON file.");
      const form = new FormData();
      form.append("file", file);
      form.append("dry_run", String(dryRun));
      return rawMultipart<LexicalImportReport>(
        `/ontology/versions/${versionId}/lexical-import/`,
        form,
      );
    },
    onSuccess: (result) => {
      setReport(result);
      if (!result.dry_run) onImported();
    },
  });

  return (
    <div className="border-b border-border bg-bg/40 px-5 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="btn inline-flex cursor-pointer items-center gap-1 text-xs">
          <FileUp size={12} />
          <span>{file?.name ?? "Choose CSV or JSON"}</span>
          <input
            type="file"
            accept=".csv,.json,text/csv,application/json"
            className="sr-only"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setReport(null);
            }}
          />
        </label>
        <button
          type="button"
          className="btn text-xs"
          disabled={!file || upload.isPending}
          onClick={() => upload.mutate(true)}
        >
          Validate
        </button>
        <button
          type="button"
          className="btn-primary text-xs"
          disabled={!file || upload.isPending || !report?.dry_run}
          onClick={() => upload.mutate(false)}
        >
          {upload.isPending ? "Importing…" : "Import labels"}
        </button>
        <span className="font-mono text-[10px] text-muted">
          subject_iri, predicate, language, value
        </span>
      </div>
      {report && (
        <div className={`mt-2 text-xs ${report.dry_run ? "text-accent" : "text-good"}`}>
          {report.dry_run ? "Valid" : "Imported"}: {report.created} new, {report.existing} existing,
          {report.duplicates_in_file} duplicate; languages {report.languages.filter(Boolean).join(", ") || "untagged"}.
        </div>
      )}
      {upload.isError && (
        <div className="mt-2 text-xs text-bad">
          {upload.error instanceof Error ? upload.error.message : String(upload.error)}
        </div>
      )}
    </div>
  );
}

function LabelMatrixDrawer({
  versionId, semver, ws, onClose,
}: { versionId: string; semver: string; ws: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [languages, setLanguages] = useState(["", "en", "es"]);
  const [newLanguage, setNewLanguage] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [aliasDrafts, setAliasDrafts] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState<string | null>(null);
  const { canEdit } = useWorkspacePermissions();

  const aiConfig = useQuery<{ enabled: boolean; credential_ids: string[] }>({
    queryKey: ["catalog-ai-credentials", ws],
    queryFn: () => rawFetch("/iam/credentials/catalog-ai/"),
    enabled: Boolean(ws && canEdit),
  });

  const subjectsQuery = useQuery({
    queryKey: ["ontology-label-subjects", versionId],
    queryFn: async () => pageResults(await rawFetch<{ results?: LabelSubject[] } | LabelSubject[]>(
      `/ontology/versions/${versionId}/lexical-subjects/`,
    )),
  });

  const lexicalQuery = useQuery({
    queryKey: ["ontology-lexical", versionId],
    queryFn: async () => pageResults(await rawFetch<{ results?: LexicalRow[] } | LexicalRow[]>(
      `/ontology/lexical-entries/?version=${versionId}&page_size=1000`,
    )),
  });

  const subjects = subjectsQuery.data ?? [];
  const entries = lexicalQuery.data ?? [];

  useEffect(() => {
    if (!subjectsQuery.data || !lexicalQuery.data) return;
    const discovered = lexicalQuery.data.map((entry) => entry.language).filter(Boolean);
    setLanguages((current) => [...new Set([...current, ...discovered])].sort((a, b) => {
      if (!a) return -1;
      if (!b) return 1;
      return a.localeCompare(b);
    }));
    const next: Record<string, string> = {};
    const nextAliases: Record<string, string> = {};
    for (const subject of subjectsQuery.data) {
      for (const language of ["", "en", "es", ...discovered]) {
        const entry = primaryLexicalEntry(lexicalQuery.data, subject.semantic_iri, language);
        const key = labelKey(subject.semantic_iri, language);
        next[key] = entry?.value ?? "";
        nextAliases[key] = lexicalQuery.data.filter((row) =>
          row.subject_iri === subject.semantic_iri && row.language === language && row.predicate === "alt_label"
        ).map((row) => row.value).join("; ");
      }
    }
    setDrafts(next);
    setAliasDrafts(nextAliases);
  }, [subjectsQuery.data, lexicalQuery.data]);

  const suggestions = useMutation<{ suggestions: Array<{ subject_iri: string; language: string; label: string }>; withheld: number; message: string }>({
    mutationFn: () => rawFetch(`/ontology/versions/${versionId}/suggest-translations/`, {
      method: "POST", body: JSON.stringify({ language: "en" }),
    }),
  });

  const save = useMutation({
    mutationFn: async () => {
      const operations: Promise<unknown>[] = [];
      for (const subject of subjects) {
        for (const language of languages) {
          const existing = primaryLexicalEntry(entries, subject.semantic_iri, language);
          const value = (drafts[labelKey(subject.semantic_iri, language)] ?? "").trim();
          if (value !== (existing?.value ?? "") && existing && !value) {
            operations.push(rawFetch(`/ontology/lexical-entries/${existing.id}/`, { method: "DELETE" }));
          } else if (value !== (existing?.value ?? "") && existing) {
            operations.push(rawFetch(`/ontology/lexical-entries/${existing.id}/`, {
              method: "PATCH",
              body: JSON.stringify({ value }),
            }));
          } else if (!existing && value) {
            operations.push(rawFetch("/ontology/lexical-entries/", {
              method: "POST",
              body: JSON.stringify({
                version: versionId,
                workspace: ws,
                subject_iri: subject.semantic_iri,
                predicate: "pref_label",
                language,
                value,
              }),
            }));
          }
          const currentAliases = entries.filter((row) =>
            row.subject_iri === subject.semantic_iri && row.language === language && row.predicate === "alt_label"
          );
          const wanted = [...new Set((aliasDrafts[labelKey(subject.semantic_iri, language)] ?? "")
            .split(";").map((item) => item.trim()).filter(Boolean))];
          for (const row of currentAliases) {
            if (!wanted.includes(row.value)) operations.push(rawFetch(`/ontology/lexical-entries/${row.id}/`, { method: "DELETE" }));
          }
          for (const alias of wanted) {
            if (!currentAliases.some((row) => row.value === alias)) operations.push(rawFetch("/ontology/lexical-entries/", {
              method: "POST", body: JSON.stringify({ version: versionId, workspace: ws,
                subject_iri: subject.semantic_iri, predicate: "alt_label", language, value: alias }),
            }));
          }
        }
      }
      await Promise.all(operations);
      return operations.length;
    },
    onSuccess: (count) => {
      setSaved(count ? `${count} label or alias change${count === 1 ? "" : "s"} saved.` : "No label or alias changes to save.");
      qc.invalidateQueries({ queryKey: ["ontology-lexical", versionId] });
      qc.invalidateQueries({ queryKey: ["ontology-graph", versionId] });
      qc.invalidateQueries({ queryKey: ["ontology-card", versionId] });
    },
  });

  function addLanguage() {
    const normalized = newLanguage.trim().toLowerCase();
    if (!normalized || !/^[a-z]{2,3}(?:-[a-z0-9]{2,8})*$/.test(normalized)) return;
    setLanguages((current) => current.includes(normalized) ? current : [...current, normalized]);
    setNewLanguage("");
  }

  const loading = subjectsQuery.isLoading || lexicalQuery.isLoading;
  const terms = subjects.filter((subject) => subject.kind === "Property" || subject.kind === "Concept");
  const missingEnglish = terms.filter((subject) => !(drafts[labelKey(subject.semantic_iri, "en")] ?? "").trim()).length;

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="flex h-full w-full max-w-[920px] flex-col overflow-hidden border-l border-border bg-card/95 shadow-2xl backdrop-blur-md">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted"><Languages size={13} /> Draft label and alias matrix</div>
            <div className="mt-1 text-sm font-medium">
              <code className="font-mono text-xs">{semver}</code>
              <span className="ml-2 text-xs text-muted">Display labels and aliases only. Semantic IRIs stay unchanged.</span>
            </div>
          </div>
          <button type="button" className="btn p-1" onClick={onClose} aria-label="Close"><X size={14} /></button>
        </div>

        <BulkLabelImport
          versionId={versionId}
          onImported={() => {
            setSaved("Bulk labels imported.");
            qc.invalidateQueries({ queryKey: ["ontology-lexical", versionId] });
            qc.invalidateQueries({ queryKey: ["ontology-graph", versionId] });
            qc.invalidateQueries({ queryKey: ["ontology-card", versionId] });
          }}
        />

        <div className="flex items-center gap-2 border-b border-border px-5 py-3">
          <span role="status" className="text-xs font-medium">{missingEnglish} terms missing an English label</span>
          {aiConfig.data?.enabled && aiConfig.data.credential_ids.length > 0 && canEdit ? (
            <button className="btn text-xs" disabled={suggestions.isPending || loading} onClick={() => suggestions.mutate()}>
              {suggestions.isPending ? "Suggesting…" : "Suggest English translations"}
            </button>
          ) : <Link to="/settings" className="text-xs text-accent">Add your LLM key to enable this</Link>}
          <span className="text-[11px] text-muted">Safe labels go to your selected provider; uncertain names are withheld. Review and save suggestions yourself.</span>
          <input
            className="input w-36 text-xs"
            value={newLanguage}
            onChange={(event) => setNewLanguage(event.target.value)}
            onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addLanguage(); } }}
            placeholder="e.g. fr or de-CH"
            aria-label="Language tag"
          />
          <button type="button" className="btn inline-flex items-center gap-1 text-xs" onClick={addLanguage} disabled={!newLanguage.trim()}>
            <Plus size={12} /> Language
          </button>
          <span className="text-[11px] text-muted">BCP 47 language tags. Empty column is the untagged default.</span>
        </div>
        {suggestions.data && <div className="border-b border-border px-5 py-2 text-xs">
          <p>{suggestions.data.suggestions.length} suggestions, {suggestions.data.withheld} withheld. Review each before applying; nothing was saved.</p>
          <div className="mt-2 flex flex-wrap gap-2">{suggestions.data.suggestions.map((item) => (
            <button key={item.subject_iri} className="btn text-xs" onClick={() => {
              setDrafts((current) => ({ ...current, [labelKey(item.subject_iri, item.language)]: item.label }));
              setSaved(null);
            }}>Use {item.label} for {subjects.find((subject) => subject.semantic_iri === item.subject_iri)?.name ?? "term"}</button>
          ))}</div>
        </div>}
        {suggestions.isError && <p role="alert" className="px-5 py-2 text-xs text-bad">{suggestions.error.message}</p>}

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-muted"><Loader2 className="animate-spin" size={16} /> Loading labels…</div>
          ) : subjectsQuery.isError || lexicalQuery.isError ? (
            <div className="p-5"><ErrorBox error={subjectsQuery.error ?? lexicalQuery.error} /></div>
          ) : subjects.length === 0 ? (
            <div className="p-6 text-sm text-muted">This draft has no classes or relationships yet.</div>
          ) : (
            <table className="w-full min-w-[720px] border-collapse text-left">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border text-[11px] uppercase text-muted">
                  <th className="w-56 px-5 py-2 font-medium">Subject</th>
                  {languages.map((language) => (
                    <th key={language || "_default"} className="min-w-40 px-2 py-2 font-medium">{language || "Default"}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subjects.map((subject) => (
                  <tr key={`${subject.kind}:${subject.semantic_iri}`} className="border-b border-border/70 align-top">
                    <td className="px-5 py-3">
                      <div className="text-sm font-medium text-fg">{subject.name}</div>
                      <div className="mt-0.5 text-[10px] uppercase text-muted">{subject.kind}</div>
                      <div className="mt-1 max-w-52 truncate font-mono text-[10px] text-muted" title={subject.semantic_iri}>{subject.semantic_iri}</div>
                    </td>
                    {languages.map((language) => {
                      const key = labelKey(subject.semantic_iri, language);
                      return (
                        <td key={language || "_default"} className="px-2 py-3">
                          <input
                            className="input min-w-0 text-sm"
                            value={drafts[key] ?? ""}
                            onChange={(event) => {
                              setSaved(null);
                              setDrafts((current) => ({ ...current, [key]: event.target.value }));
                            }}
                            placeholder={language ? `Label @${language}` : subject.name}
                            aria-label={`${subject.name} ${language || "default"} label`}
                          />
                          <input className="input mt-1 min-w-0 text-xs" value={aliasDrafts[key] ?? ""}
                            onChange={(event) => { setSaved(null); setAliasDrafts((current) => ({ ...current, [key]: event.target.value })); }}
                            placeholder="Aliases separated by ;"
                            aria-label={`${subject.name} ${language || "default"} aliases`} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-5 py-3">
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1 text-sm"
            disabled={!ws || !canEdit || loading || save.isPending || subjects.length === 0}
            onClick={() => { setSaved(null); save.mutate(); }}
          >
            {save.isPending ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
            {save.isPending ? "Saving…" : "Save labels and aliases"}
          </button>
          <button type="button" className="btn text-sm" onClick={onClose}>Close</button>
          {saved && <span className="text-xs text-good">{saved}</span>}
          {save.isError && <span className="text-xs text-bad">{save.error instanceof Error ? save.error.message : String(save.error)}</span>}
        </div>
      </aside>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Card drawer                                                                 //
// --------------------------------------------------------------------------- //

function CardDrawer({ versionId, onClose }: { versionId: string; onClose: () => void }) {
  const ws = useApp((s) => s.workspaceId);
  const [graphMode, setGraphMode] = useState<"preview" | "explore" | "instances">("preview");
  const card = useQuery({
    queryKey: ["ontology-card", versionId],
    queryFn: () =>
      rawFetch<{ version_id: string; semver: string; markdown: string }>(
        `/ontology/versions/${versionId}/card/?fmt=json`,
      ),
  });

  const graph = useQuery<OntologyGraph>({
    queryKey: ["ontology-graph", versionId],
    queryFn: () => rawFetch(`/ontology/versions/${versionId}/graph/`),
  });

  // Phase 13.2 / G7 — the *instance* KG: the actual entities + relations
  // materialized into the graph store from ingested documents, as opposed to
  // the T-Box *schema* graph above. Fetched lazily (only when the Instances
  // tab is selected) and adapted to the OntologyGraphExplorer node/edge shape.
  const instances = useQuery<{
    nodes: { id: string; labels?: string[]; properties?: Record<string, unknown> }[];
    edges: { id: string; type: string; src: string; dst: string }[];
    node_count: number;
    edge_count: number;
  }>({
    queryKey: ["graph-instances", ws],
    queryFn: () => rawFetch(`/graph/instances/${ws}/?limit=250`),
    enabled: graphMode === "instances" && Boolean(ws),
  });

  const instanceGraph: OntologyGraph | null = instances.data
    ? {
        nodes: instances.data.nodes.map((n) => ({
          id: n.id,
          label:
            (n.properties?.name as string) ||
            (n.properties?.label as string) ||
            n.labels?.[0] ||
            n.id,
        })),
        edges: instances.data.edges.map((e) => ({
          id: e.id,
          label: e.type,
          source: e.src,
          target: e.dst,
        })),
      }
    : null;

  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside
            className="
              flex h-full w-full max-w-[760px]
              flex-col overflow-hidden
              bg-card/95
              backdrop-blur-md
              border-l border-border
              shadow-2xl
            "
          >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted">Ontology card</div>
            <div className="text-sm font-medium">
              {card.data?.semver ?? "Loading…"}{" "}
              <code className="ml-2 font-mono text-[11px] text-muted">{versionId.slice(0, 8)}</code>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn inline-flex items-center gap-1 text-xs"
              onClick={() =>
                void downloadBlob(
                  `/ontology/versions/${versionId}/card/`,
                  `ontology-${card.data?.semver ?? versionId.slice(0, 8)}.md`,
                )
              }
            >
              <Download size={12} /> .md
            </button>
            <button type="button" className="btn p-1" onClick={onClose} aria-label="Close">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {graph.isSuccess && (
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-end">
                <div className="inline-flex rounded-md border border-border p-0.5 text-[11px]" style={{ backgroundColor: "white" }}>
                  {(["preview", "explore", "instances"] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={"rounded px-2.5 py-1 " + (graphMode === m ? "bg-accent/60 text-fg" : "text-muted hover:text-fg")}
                      onClick={() => setGraphMode(m)}
                    >
                      {m === "preview" ? "Preview" : m === "explore" ? "Explore" : "Instances"}
                    </button>
                  ))}
                </div>
              </div>
              {graphMode === "preview"
                ? <OntologyMiniGraph graph={graph.data} />
                : graphMode === "explore"
                ? <OntologyGraphExplorer graph={graph.data} />
                : instances.isLoading
                ? <div className="py-10 text-center text-sm text-muted">Loading instance graph…</div>
                : instances.isError
                ? <ErrorBox error={instances.error} />
                : instanceGraph && instanceGraph.nodes.length > 0
                ? (
                    <>
                      <div className="mb-2 text-[11px] text-muted">
                        {instances.data?.node_count ?? 0} entities · {instances.data?.edge_count ?? 0} relations materialized from ingested documents.
                      </div>
                      <OntologyGraphExplorer graph={instanceGraph} />
                    </>
                  )
                : <div className="py-10 text-center text-sm text-muted">No instances materialized yet — ingest documents in Catalog to populate the knowledge graph.</div>}
            </div>
          )}
          {card.isLoading && <div className="text-sm text-muted">Loading card…</div>}
          {card.isError && <ErrorBox error={card.error} />}
          {card.data && (
            <article className="prose-onb">
              <Markdown source={card.data.markdown} />
            </article>
          )}
        </div>
      </aside>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Generate-from-domain tab                                                    //
// --------------------------------------------------------------------------- //

function GenerateTab({ ws, canEdit, onCreated }: { ws: string | null; canEdit: boolean; onCreated: () => void }) {
  const qc = useQueryClient();
  const [domain, setDomain] = useState("");
  const [industry, setIndustry] = useState("");
  const [model, setModel] = useState("stub");
  const [termSetVersionIds, setTermSetVersionIds] = useState<string[]>([]);
  const [packIds, setPackIds] = useState<string[]>([]);
  const [sampleTurtle, setSampleTurtle] = useState("");
  const [lastResult, setLastResult] = useState<GenerateResponse | null>(null);

  const generate = useMutation({
    mutationFn: () =>
      rawFetch<GenerateResponse>("/ontology/generate/", {
        method: "POST",
        body: JSON.stringify({
          workspace: ws,
          domain,
          industry: packIds.length ? (industry || "general") : (industry || undefined),
          model: termSetVersionIds.length ? "stub" : model || "stub",
          term_set_version_ids: termSetVersionIds.length ? termSetVersionIds : undefined,
          pack_ids: packIds.length ? packIds : undefined,
          sample_ttl: packIds.length && sampleTurtle.trim() ? sampleTurtle.trim() : undefined,
        }),
      }),
    onSuccess: (r) => {
      setLastResult(r);
      qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
    },
  });

  const wsReady = Boolean(ws);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_420px]">
      <div className="card text-sm text-muted">
        <p className="mb-2">
          Describe your domain or pin approved term-set versions. Either path creates a DRAFT ontology
          you can refine in{" "}
          <Link to="/evals" className="text-accent underline">Evals</Link>{" "}
          before publishing.
        </p>
        <p className="text-xs">
          Tip: for regulated industries, use “Start from standard” to review
          the exact source, version, licence, and supported adoption mode before
          adding mappings or customer-supplied content.
        </p>
      </div>
      <aside className="card space-y-3">
        <div className="flex items-center gap-2">
          <Wand2 size={16} className="text-accent" />
          <h3 className="text-sm font-semibold">Generate from domain</h3>
        </div>
        <label className="block">
          <div className="mb-1 text-xs uppercase text-muted">Industry context</div>
          <select className="input text-xs" value={industry}
            onChange={(event) => { setIndustry(event.target.value); setPackIds([]); setSampleTurtle(""); }}
            disabled={!canEdit || !wsReady || generate.isPending}>
            {INDUSTRIES.map((item) => <option key={item.id || "_general"} value={item.id}>{item.label}</option>)}
          </select>
          <span className="mt-1 block text-[11px] text-muted">The selected industry controls eligible packs. It does not import a standard or configure a runtime pipeline.</span>
        </label>
        <TermSetSelector workspace={ws} selected={termSetVersionIds} onChange={setTermSetVersionIds} disabled={!canEdit || generate.isPending} />
        <EligiblePackSelector workspace={ws} industry={industry || "general"} selected={packIds} onChange={setPackIds} disabled={!canEdit || generate.isPending} />
        {termSetVersionIds.length > 0 && <p className="text-xs text-muted">Selected approved versions create a draft without model egress. The domain description is optional on this path.</p>}
        {packIds.length > 0 && <>
          <p className="text-xs text-muted">Pack composition remains a draft. A non-vacuous SHACL Core sample must pass before publication. Validate it in the draft's Shapes panel. Pinned pack shapes must remain unchanged; create a new draft to change that contract.</p>
          <label className="block text-xs">Optional synthetic sample Turtle for the build gate
            <textarea className="input mt-1 min-h-24 font-mono text-xs" value={sampleTurtle}
              onChange={(event) => setSampleTurtle(event.target.value)}
              disabled={!canEdit || generate.isPending} maxLength={100000}
              placeholder="@prefix ex: <urn:synthetic:> ." />
          </label>
        </>}

        <label className="block">
          <div className="mb-1 text-xs uppercase text-muted">Domain description</div>
          <textarea
            className="input min-h-[120px] font-mono text-xs"
            placeholder="e.g. We process auto insurance claims with policies, claimants, adjusters, and supporting documents."
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            disabled={!canEdit || !wsReady || generate.isPending}
          />
        </label>

        {termSetVersionIds.length === 0 && <label className="block">
          <div className="mb-1 text-xs uppercase text-muted">Model</div>
          <select
            className="input text-xs"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={!canEdit || !wsReady || generate.isPending}
          >
            <option value="stub">stub (deterministic, free)</option>
            <option value="openai:gpt-4o-mini">openai:gpt-4o-mini</option>
            <option value="openai:gpt-4o">openai:gpt-4o</option>
            <option value="anthropic:claude-3-5-sonnet">anthropic:claude-3-5-sonnet</option>
          </select>
          <div className="mt-1 text-[11px] text-muted">
            LLM models route through your BYOK credential and are billed
            against your workspace.
          </div>
        </label>}

        <button
          className="btn-primary w-full"
          disabled={!canEdit || !wsReady || generate.isPending || (!domain.trim() && !termSetVersionIds.length) || (packIds.length > 0 && !termSetVersionIds.length)}
          onClick={() => generate.mutate()}
        >
          {generate.isPending ? "Generating…" : "Generate draft"}
        </button>

        {generate.isError && <ErrorBox error={generate.error} />}

        {lastResult && (
          <div className="rounded border border-border bg-bg/60 p-2 text-xs">
            <div className="flex items-center gap-2">
              <Hexagon size={12} className="text-accent" />
              <span className="font-semibold">Draft created</span>
              <StatusPill status={lastResult.status} />
            </div>
            <div className="mt-1 font-mono text-[11px] text-muted">{lastResult.semver}</div>
            <div className="mt-1">
              {lastResult.entities_created} entities ·{" "}
              {lastResult.relations_created} relations ·{" "}
              {lastResult.taxonomies_created} taxonomies
            </div>
            <button type="button" className="mt-2 text-accent underline" onClick={onCreated}>
              Open versions list →
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Standards tab                                                               //
// --------------------------------------------------------------------------- //

function StandardsTab({ ws, onCreated }: { ws: string | null; onCreated: () => void }) {
  const qc = useQueryClient();
  const [industry, setIndustry] = useState("");
  const [scope, setScope] = useState<"" | "bundled" | "external">("");
  const [importPrefill, setImportPrefill] = useState("");

  const list = useQuery({
    queryKey: ["ontology-standards", industry, scope],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (industry) qs.set("industry", industry);
      if (scope) qs.set("scope", scope);
      const q = qs.toString();
      return rawFetch<{ standards: StandardSummary[] }>(
        `/ontology/standards/${q ? `?${q}` : ""}`,
      );
    },
  });

  const groups = useMemo(() => {
    const by = new Map<string, StandardSummary[]>();
    for (const s of list.data?.standards ?? []) {
      if (!by.has(s.industry)) by.set(s.industry, []);
      by.get(s.industry)!.push(s);
    }
    return Array.from(by.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [list.data]);

  const seed = useMutation({
    mutationFn: (standard_id: string) =>
      rawFetch<{ version_id: string; semver: string }>(
        "/ontology/standards/seed/",
        { method: "POST", body: JSON.stringify({ workspace: ws, standard_id }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
      onCreated();
    },
  });

  const industries = useMemo(() => {
    const s = new Set<string>(["finance", "insurance", "healthcare", "cross-domain", "governance"]);
    for (const x of list.data?.standards ?? []) s.add(x.industry);
    return Array.from(s).sort();
  }, [list.data]);

  return (
    <div className="space-y-4">
      <div className="card flex flex-wrap items-end gap-3">
        <label className="block">
          <div className="mb-1 text-xs uppercase text-muted">Industry</div>
          <select
            className="input text-xs"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
          >
            <option value="">All industries</option>
            {industries.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="mb-1 text-xs uppercase text-muted">Scope</div>
          <select
            className="input text-xs"
            value={scope}
            onChange={(e) => setScope(e.target.value as typeof scope)}
          >
            <option value="">All</option>
            <option value="bundled">Bundled records</option>
            <option value="external">External (reference)</option>
          </select>
        </label>
        <div className="ml-auto text-xs text-muted">
          {list.data?.standards.length ?? 0} standard(s)
        </div>
      </div>

      <ImportOntologyPanel ws={ws} prefill={importPrefill} onCreated={onCreated} />

      {seed.isError && <ErrorBox error={seed.error} />}
      {list.isError && <ErrorBox error={list.error} />}
      {list.isLoading && <div className="card text-sm text-muted">Loading standards…</div>}

      {groups.map(([ind, items]) => (
        <section key={ind}>
          <h3 className="mb-2 text-xs uppercase tracking-wide text-muted">{ind}</h3>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {items.map((s) => (
              <StandardCard
                key={s.id}
                spec={s}
                wsReady={Boolean(ws)}
                pending={seed.isPending && seed.variables === s.id}
                onSeed={() => seed.mutate(s.id)}
                onImport={() => {
                  setImportPrefill(s.display_name);
                  document.getElementById("byo-import")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Bring-your-own-ontology — the unblocker for unsupported public/private
// ontologies. Anything you can export as OWL / Turtle / RDF-XML / JSON-LD /
// N-Triples can be imported: we create an empty DRAFT version and load the file
// into it, after which it behaves like any other ontology (validate, export,
// publish, enhance with a complementary pack).
// --------------------------------------------------------------------------- //

const IMPORT_FORMATS: { value: string; label: string; ext: string }[] = [
  { value: "auto",    label: "Auto-detect from file", ext: "" },
  { value: "xml",     label: "OWL / RDF-XML",         ext: ".owl, .rdf, .xml" },
  { value: "turtle",  label: "Turtle",                ext: ".ttl" },
  { value: "json-ld", label: "JSON-LD",               ext: ".jsonld, .json" },
  { value: "n3",      label: "Notation3",             ext: ".n3" },
  { value: "nt",      label: "N-Triples",             ext: ".nt" },
];

function ImportOntologyPanel({
  ws, prefill, onCreated,
}: { ws: string | null; prefill: string; onCreated: () => void }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [fmt, setFmt] = useState("auto");
  const [result, setResult] = useState<{
    version: string;
    summary: Record<string, number>;
    import_job?: {
      id: string;
      source_filename: string;
      source_size: number;
      source_sha256: string;
      canonical_sha256: string;
      parse_warnings: string[];
    } | null;
  } | null>(null);

  // When an external standard card asks to import, prefill the name and open.
  useEffect(() => {
    if (prefill) { setName(prefill); setOpen(true); }
  }, [prefill]);

  const run = useMutation({
    mutationFn: async () => {
      if (!ws) throw new Error("Pick a workspace from the header first.");
      if (!file) throw new Error("Choose an ontology file to import.");
      // 1) Create an empty DRAFT version to receive the import.
      const draft = await rawFetch<OntologyVersion>("/ontology/versions/", {
        method: "POST",
        body: JSON.stringify({
          workspace: ws,
          semver: `0.1.0-import-${Date.now().toString(36)}`,
          notes: name ? `Imported ontology: ${name}` : "Imported ontology",
        }),
      });
      // 2) Upload the file into that draft.
      const form = new FormData();
      form.append("file", file);
      const q = fmt && fmt !== "auto" ? `?format=${fmt}` : "";
      return rawMultipart<{
        version: string;
        summary: Record<string, number>;
        import_job?: {
          id: string;
          source_filename: string;
          source_size: number;
          source_sha256: string;
          canonical_sha256: string;
          parse_warnings: string[];
        } | null;
      }>(
        `/semantic/import/ontology/${draft.id}/${q}`,
        form,
      );
    },
    onSuccess: (res) => setResult(res),
  });

  return (
    <section id="byo-import" className="card border-accent/30">
      <button
        type="button"
        className="flex w-full items-center gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <UploadCloud size={16} className="text-accent" />
        <span className="text-sm font-semibold">Bring your own ontology</span>
        <span className="text-xs text-muted">— don’t see the pack you need? Import any public or private ontology file.</span>
        <span className="ml-auto text-xs text-accent">{open ? "Hide" : "Open"}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <p className="text-xs text-muted">
            Works for anything you can export as <strong>OWL / RDF-XML, Turtle,
            JSON-LD, Notation3 or N-Triples</strong> — including license-gated
            vocabularies (e.g. SNOMED CT, FIBO Pro, ICD-11). Convert the source to
            one of these formats, upload it, and we create a draft version you can
            validate, publish, export and enhance with a complementary pack.
            Your file is parsed inside your own tenant.
          </p>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="block md:col-span-1">
              <div className="mb-1 text-xs uppercase text-muted">Name (optional)</div>
              <input
                className="input text-xs"
                placeholder="e.g. SNOMED CT subset"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="block md:col-span-1">
              <div className="mb-1 text-xs uppercase text-muted">Format</div>
              <select className="input text-xs" value={fmt} onChange={(e) => setFmt(e.target.value)}>
                {IMPORT_FORMATS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}{f.ext ? ` (${f.ext})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label className="block md:col-span-1">
              <div className="mb-1 text-xs uppercase text-muted">File</div>
              <input
                type="file"
                accept=".owl,.rdf,.xml,.ttl,.jsonld,.json,.n3,.nt"
                className="block w-full text-xs file:mr-2 file:rounded file:border file:border-border file:bg-bg file:px-2 file:py-1 file:text-xs"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </label>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-1 text-xs"
              disabled={!ws || !file || run.isPending}
              onClick={() => { setResult(null); run.mutate(); }}
              title={!ws ? "Pick a workspace first" : "Create a draft version and import the file"}
            >
              <FileUp size={12} />
              {run.isPending ? "Importing…" : "Import to draft"}
            </button>
            {result && (
              <button
                type="button"
                className="btn inline-flex items-center gap-1 text-xs"
                onClick={onCreated}
                title="Open the Versions tab to validate, publish or export"
              >
                <Hexagon size={12} /> View in versions
              </button>
            )}
            <span className="ml-auto text-[11px] text-muted">
              Can’t produce a file? <a className="text-accent hover:underline"
                href="mailto:support@ontoligent.com?subject=Ontology%20pack%20request">Request a pack →</a>
            </span>
          </div>

          {run.isError && <ErrorBox error={run.error} />}
          {result && (
            <div className="rounded border border-good/40 bg-good/5 px-3 py-2 text-xs">
              <div className="mb-1 font-semibold text-good">Imported into a draft version.</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted">
                {Object.entries(result.summary ?? {}).map(([k, v]) => (
                  <span key={k}><strong className="text-fg">{v}</strong> {k.replace(/_/g, " ")}</span>
                ))}
              </div>
              <div className="mt-1 text-muted">
                Draft <code className="font-mono">{result.version.slice(0, 8)}</code> — validate it (reasoner / SHACL / CQs),
                then publish from the Versions tab.
              </div>
              {result.import_job && (
                <div className="mt-2 border-t border-good/20 pt-2 text-[11px] text-muted">
                  <div>
                    Preserved source: <code>{result.import_job.source_filename}</code>
                    {" · "}{result.import_job.source_size.toLocaleString()} bytes
                    {" · SHA-256 "}<code>{result.import_job.source_sha256.slice(0, 12)}…</code>
                  </div>
                  <div>
                    Canonical RDF: <code>{result.import_job.canonical_sha256.slice(0, 12)}…</code>
                  </div>
                  {result.import_job.parse_warnings.length > 0 && (
                    <ul className="mt-1 list-disc space-y-0.5 pl-4 text-warn">
                      {result.import_job.parse_warnings.map((warning) => (
                        <li key={warning}>{warning}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function StandardCard({
  spec, wsReady, pending, onSeed, onImport,
}: {
  spec: StandardSummary;
  wsReady: boolean;
  pending: boolean;
  onSeed: () => void;
  onImport: () => void;
}) {
  const bundled = spec.scope === "bundled";
  const seedable = bundled && spec.seedable;
  return (
    <div className="card flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold leading-tight">{spec.display_name}</div>
          <div className="mt-0.5 text-[11px] text-muted">
            <code className="font-mono">{spec.id}</code> · {spec.license}
          </div>
        </div>
        <span className={seedable ? "pill-good" : "pill"}>
          {seedable ? "seedable" : spec.availability === "unavailable" ? "quarantined" : spec.scope}
        </span>
      </div>
      <p className="text-xs text-muted">{spec.description}</p>
      {bundled && (
        <div className="text-[11px] text-muted">
          {spec.entity_count} entities · {spec.relation_count} relations
          {spec.taxonomy_count ? ` · ${spec.taxonomy_count} taxonomies` : ""}
        </div>
      )}
      {spec.integration_notes && (
        <div className="rounded bg-bg/60 px-2 py-1 text-[11px] text-muted">
          {spec.integration_notes}
        </div>
      )}
      {!seedable && spec.unavailable_reason && (
        <div className="rounded border border-warn/30 bg-warn/5 px-2 py-1 text-[11px] text-warn">
          {spec.unavailable_reason}
        </div>
      )}
      <div className="mt-1 flex items-center gap-2">
        {seedable ? (
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={!wsReady || pending}
            onClick={onSeed}
            title={wsReady ? "Create a DRAFT ontology version from this standard"
                           : "Pick a workspace first"}
          >
            {pending ? "Seeding…" : "Seed draft"}
          </button>
        ) : bundled ? (
          <>
            <button type="button" className="btn text-xs" disabled title={spec.unavailable_reason}>
              Seeding unavailable
            </button>
            <Link to="/studio/composer" className="btn inline-flex items-center gap-1 text-xs">
              Review adoption
            </Link>
          </>
        ) : (
          <button
            type="button"
            className="btn inline-flex items-center gap-1 text-xs"
            onClick={onImport}
            title="License-gated / reference-only. Convert it to OWL / Turtle / RDF-XML / JSON-LD and import the file here."
          >
            <FileUp size={12} /> Import a file…
          </button>
        )}
        {spec.source_url && (
          <a
            href={spec.source_url}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-accent hover:underline"
          >
            source <ExternalLink size={10} />
          </a>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Validate drawer — Reasoner / SHACL / CQs / SPARQL                          //
// --------------------------------------------------------------------------- //

type VTab = "readiness" | "reason" | "shacl" | "cqs" | "sparql";

interface ReasonReport {
  consistent: boolean;
  profile: string;
  asserted_classes: number;
  inferred_triples: number;
  // BE serializes dataclass tuples → JSON arrays [child, parent].
  inferred_subclasses: Array<[string, string]>;
  equivalent_classes: Array<[string, string]>;
  unsatisfiable: string[];
  issues: string[];
  error?: string;
}

interface CqRow {
  id: string;
  text: string;
  expected_kind: string;
  status: string;
  last_run_at: string | null;
  last_result_count: number | null;
  last_error: string;
  sparql: string;
}

interface SparqlResp {
  kind: "select" | "ask" | "construct";
  columns: string[];
  rows: Array<Record<string, string>>;
  boolean: boolean | null;
}

interface ShaclSampleReport {
  conforms: boolean;
  passed?: boolean;
  non_vacuous?: boolean;
  focus_counts?: Array<{ shape: string; focus_nodes: number }>;
  violation_count: number;
  shape_source: "saved" | "generated" | "pinned_core";
  shape_set_id: string | null;
  violations: Array<{
    focus_node: string;
    result_path: string;
    message: string;
    severity: string;
  }>;
  review_plan: {
    status: "ready_for_sme_review" | "no_findings";
    raw_finding_count: number;
    concept_count: number;
    action_counts: Record<string, number>;
    principle: string;
    concepts: Array<{
      focus_node: string;
      display_name: string;
      finding_count: number;
      actions: Array<{
        code: string;
        title: string;
        guidance: string;
        decision: string;
        result_path: string;
        message: string;
        severity: string;
      }>;
    }>;
  };
}

interface ReleasePolicyReport {
  passed: boolean;
  checks: Record<string, {
    required: boolean;
    passed: boolean;
    error?: string;
    skipped?: string;
    blockers?: Record<string, string[]>;
    [key: string]: unknown;
  }>;
}

function ValidateDrawer({
  versionId, onClose,
}: { versionId: string; onClose: () => void }) {
  const [tab, setTab] = useState<VTab>("readiness");
  return (
    <div className="fixed inset-0 z-40 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside
            className="flex h-full w-full max-w-[1040px]
          flex-col overflow-hidden
          bg-card/95
          backdrop-blur-md
          border-l border-border
          shadow-2xl"
            
          >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted">Release readiness</div>
            <div className="text-sm font-medium">
              <code className="font-mono text-[11px] text-muted">{versionId.slice(0, 8)}</code>
            </div>
          </div>
          <button type="button" className="btn p-1" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div className="flex items-center gap-1 border-b border-border px-3">
          <VTabBtn active={tab === "readiness"} onClick={() => setTab("readiness")} icon={<BadgeCheck size={12} />}>Readiness</VTabBtn>
          <VTabBtn active={tab === "reason"} onClick={() => setTab("reason")} icon={<Sparkles size={12} />}>Reasoner</VTabBtn>
          <VTabBtn active={tab === "shacl"} onClick={() => setTab("shacl")} icon={<ShieldCheck size={12} />}>SHACL</VTabBtn>
          <VTabBtn active={tab === "cqs"} onClick={() => setTab("cqs")} icon={<ListChecks size={12} />}>Competency questions</VTabBtn>
          <VTabBtn active={tab === "sparql"} onClick={() => setTab("sparql")} icon={<Terminal size={12} />}>SPARQL</VTabBtn>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {tab === "readiness" && <ReleaseReadinessPanel versionId={versionId} />}
          {tab === "reason" && <ReasonPanel versionId={versionId} />}
          {tab === "shacl" && <ShaclPanel versionId={versionId} />}
          {tab === "cqs" && <CqPanel versionId={versionId} />}
          {tab === "sparql" && <SparqlPanel versionId={versionId} />}
        </div>
      </aside>
    </div>
  );
}

function VTabBtn({
  active, onClick, icon, children,
}: { active: boolean; onClick: () => void; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "inline-flex items-center gap-1 border-b-2 px-3 py-2 text-xs " +
        (active ? "border-accent text-fg" : "border-transparent text-muted hover:text-fg")
      }
    >
      {icon} {children}
    </button>
  );
}

function ReasonPanel({ versionId }: { versionId: string }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => rawFetch<ReasonReport>(`/ontology/versions/${versionId}/reason/`, {
      method: "POST", body: "{}",
    }),
  });
  const r = m.data;
  const [repairOpen, setRepairOpen] = useState(false);
  const [repairModel, setRepairModel] = useState<string>("stub");

  // Auto-run on mount: users were opening the drawer and seeing a blank
  // panel because they had to click "Run reasoner" first. Run it for them.
  useEffect(() => { m.mutate(); }, [versionId]);

  // Single-click Approve & Publish when reasoning is clean.
  const versionStatusQ = useQuery({
    queryKey: ["ontology-version-status", versionId],
    queryFn: () => rawFetch<{ id: string; semver: string; status: string }>(
      `/ontology/versions/${versionId}/`,
    ),
  });
  const publish = useMutation({
    mutationFn: () => rawFetch<{ status: string; semver: string }>(
      `/ontology/versions/${versionId}/publish/`,
      { method: "POST", body: "{}" },
    ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
      qc.invalidateQueries({ queryKey: ["ontology-version-status", versionId] });
      qc.invalidateQueries({ queryKey: ["experts-diagnostics"] });
    },
  });
  const releaseCheck = useMutation({
    mutationFn: () => rawFetch<ReleasePolicyReport>(
      `/ontology/versions/${versionId}/release-check/`,
      { method: "POST", body: "{}" },
    ),
  });
  const isDraft = versionStatusQ.data?.status === "draft";
  const cleanReport = !!(r && r.consistent && r.unsatisfiable.length === 0 && !r.error);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted">
          OWL-RL closure via <code>owlrl</code>. Detects inconsistencies, derives
          subclass/equivalence triples, and flags unsatisfiable classes.
          {" "}<span className="text-muted/80">
            <code>owl:Nothing</code> is excluded from results (vacuously
            unsatisfiable by definition).
          </span>
        </p>
        <button className="btn-primary inline-flex items-center gap-1 text-xs"
                onClick={() => m.mutate()} disabled={m.isPending}>
          <Play size={12} /> {m.isPending ? "Reasoning…" : "Re-run reasoner"}
        </button>
      </div>
      {m.isError && <ErrorBox error={m.error} />}
      {r && (
        <div className="space-y-2 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className={r.consistent ? "pill-good" : "pill-bad"}>
              {r.consistent ? "consistent" : "INCONSISTENT"}
            </span>
            <span className="pill">profile: {r.profile}</span>
            <span className="pill">asserted classes: {r.asserted_classes}</span>
            <span className="pill">inferred triples: {r.inferred_triples}</span>
            {(r.unsatisfiable.length > 0 || !r.consistent) && (
              <button
                type="button"
                className="btn ml-auto inline-flex items-center gap-1 border-bad/40 text-[11px] text-bad"
                onClick={() => setRepairOpen(true)}
                title="Open the repair assistant"
              >
                <ShieldCheck size={12} /> Repair…
              </button>
            )}
          </div>
          {cleanReport && isDraft && (
            <div className="card border-good/30 bg-good/5 text-[11px]">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-semibold text-good">
                    Reasoner clean
                  </div>
                  <div className="mt-0.5 text-muted">
                    Run the complete workspace release policy before activation.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn inline-flex items-center gap-1 text-xs"
                    onClick={() => releaseCheck.mutate()}
                    disabled={releaseCheck.isPending}
                  >
                    <ListChecks size={12} /> {releaseCheck.isPending ? "Checking…" : "Run release gate"}
                  </button>
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center gap-1 text-xs"
                    onClick={() => {
                      if (window.confirm(
                        `Publish ${versionStatusQ.data?.semver ?? versionId.slice(0, 8)}?\n\n` +
                        "The server will rerun every required release check before activation.",
                      )) publish.mutate();
                    }}
                    disabled={publish.isPending || releaseCheck.data?.passed !== true}
                    title={releaseCheck.data?.passed ? "Publish this validated draft" : "Run and pass the complete release gate first"}
                  >
                    <BadgeCheck size={12} />
                    {publish.isPending ? "Publishing…" : "Approve & Publish"}
                  </button>
                </div>
              </div>
              {releaseCheck.isError && <div className="mt-2"><ErrorBox error={releaseCheck.error} /></div>}
              {releaseCheck.data && (
                <div className="mt-2 space-y-2">
                  <div className="grid gap-1 sm:grid-cols-2">
                    {Object.entries(releaseCheck.data.checks).map(([name, check]) => (
                      <div key={name} className="rounded border border-border bg-bg/50 px-2 py-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="uppercase text-muted">{name}</span>
                          <span className={check.passed ? "pill-good" : "pill-bad"} title={check.error || check.skipped || ""}>
                            {check.passed ? (check.required ? "passed" : "not required") : "blocked"}
                          </span>
                        </div>
                        {check.error && <div className="mt-1 text-[11px] text-bad">{check.error}</div>}
                        {check.blockers && Object.values(check.blockers).some((values) => values.length > 0) && (
                          <div className="mt-2 space-y-1 text-[11px] text-muted">
                            {Object.entries(check.blockers).filter(([, values]) => values.length > 0).map(([key, values]) => (
                              <div key={key}>
                                <span className="uppercase">{key.replaceAll("_", " ")}</span>: {values.join(", ")}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {publish.isError && (
                <div className="mt-1 text-bad">
                  {publish.error instanceof Error ? publish.error.message : String(publish.error)}
                </div>
              )}
              {publish.isSuccess && (
                <div className="mt-1 text-good">
                  Published — retrieval readiness will refresh.
                </div>
              )}
            </div>
          )}
          {cleanReport && !isDraft && versionStatusQ.data && (
            <div className="card text-[11px] text-muted">
              Reasoner clean. This version is{" "}
              <code>{versionStatusQ.data.status}</code> — no publish needed.
            </div>
          )}
          {r.error && <ErrorBox error={r.error} />}
          {r.unsatisfiable.length > 0 && (
            <div className="card border-bad/40 text-[11px]">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-bad">Unsatisfiable classes</div>
                <select
                  className="input text-[11px]"
                  value={repairModel}
                  onChange={(e) => setRepairModel(e.target.value)}
                  title="Model used to propose the repair"
                >
                  <option value="stub">stub (deterministic)</option>
                  <option value="openai:gpt-4o-mini">openai:gpt-4o-mini</option>
                  <option value="openai:gpt-4o">openai:gpt-4o</option>
                  <option value="anthropic:claude-3-5-sonnet">anthropic:claude-3-5-sonnet</option>
                </select>
              </div>
              <ul className="mt-1 list-disc pl-5">
                {r.unsatisfiable.map((u) => <li key={u}><code>{u}</code></li>)}
              </ul>
              <div className="mt-2 text-muted">
                Use the <strong>Repair</strong> button to preview a fix and
                confirm before a new DRAFT version is created.
              </div>
            </div>
          )}
          <Section title={`Inferred subclassings (${r.inferred_subclasses.length})`}>
            {r.inferred_subclasses.length === 0 && <span className="text-muted">— none —</span>}
            {r.inferred_subclasses.map(([child, parent], i) => (
              <div key={i} className="font-mono text-[11px]">
                <code>{child}</code> <span className="text-muted">⊑</span> <code>{parent}</code>
              </div>
            ))}
          </Section>
          <Section title={`Equivalent classes (${r.equivalent_classes.length})`}>
            {r.equivalent_classes.length === 0 && <span className="text-muted">— none —</span>}
            {r.equivalent_classes.map(([a, b], i) => (
              <div key={i} className="font-mono text-[11px]">
                <code>{a}</code> <span className="text-muted">≡</span> <code>{b}</code>
              </div>
            ))}
          </Section>
          {r.issues.length > 0 && (
            <Section title={`Issues (${r.issues.length})`}>
              {r.issues.map((m, i) => <div key={i} className="text-[11px] text-warn">⚠ {m}</div>)}
            </Section>
          )}
        </div>
      )}
      {repairOpen && (
        <RepairDialog
          versionId={versionId}
          model={repairModel}
          onClose={() => setRepairOpen(false)}
          onApplied={() => {
            setRepairOpen(false);
            qc.invalidateQueries({ queryKey: ["table", "/ontology/versions/"] });
            m.mutate();
          }}
        />
      )}
    </div>
  );
}

interface RepairAction {
  kind: "remove_entity" | "remove_relation" | "rename_entity";
  target: string;
  reason: string;
  details?: Record<string, unknown>;
}
interface RepairProposal {
  needs_repair: boolean;
  consistent: boolean;
  unsatisfiable: string[];
  issues: string[];
  explanations: Record<string, string[]>;
  actions: RepairAction[];
  summary: string;
  model_used: string;
}
interface RepairApplied {
  applied: true;
  proposal: RepairProposal;
  new_version: { id: string; semver: string; status: string };
}

function RepairDialog({
  versionId, model, onClose, onApplied,
}: {
  versionId: string;
  model: string;
  onClose: () => void;
  onApplied: (resp: RepairApplied) => void;
}) {
  const [editedActions, setEditedActions] = useState<RepairAction[] | null>(null);

  const preview = useQuery<RepairProposal>({
    queryKey: ["ontology-repair-preview", versionId, model],
    queryFn: () => rawFetch<RepairProposal>(
      `/ontology/versions/${versionId}/repair/`,
      { method: "POST", body: JSON.stringify({ model }) },
    ),
  });

  const apply = useMutation({
    mutationFn: () => rawFetch<RepairApplied>(
      `/ontology/versions/${versionId}/repair/`,
      {
        method: "POST",
        body: JSON.stringify({
          confirm: true,
          model,
          actions: editedActions ?? preview.data?.actions ?? [],
        }),
      },
    ),
    onSuccess: (resp) => onApplied(resp),
  });

  const proposal = preview.data;
  const actions = editedActions ?? proposal?.actions ?? [];

  const toggle = (idx: number) => {
    const next = (editedActions ?? proposal?.actions ?? []).slice();
    next.splice(idx, 1);
    setEditedActions(next);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg border border-border bg-card/95 backdrop-blur-md shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted">Reasoner repair</div>
            <div className="text-sm font-medium">Review &amp; confirm</div>
          </div>
          <button type="button" className="btn p-1" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto px-4 py-3 text-xs">
          {preview.isLoading && <div className="text-muted">Analyzing ontology…</div>}
          {preview.isError && <ErrorBox error={preview.error} />}
          {proposal && (
            <>
              <div className="rounded border border-border bg-bg/60 p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={proposal.consistent ? "pill-good" : "pill-bad"}>
                    {proposal.consistent ? "consistent" : "INCONSISTENT"}
                  </span>
                  <span className="pill">model: {proposal.model_used}</span>
                  {proposal.unsatisfiable.length > 0 && (
                    <span className="pill-bad">
                      {proposal.unsatisfiable.length} unsatisfiable
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[12px]">{proposal.summary}</p>
              </div>
              {Object.keys(proposal.explanations).length > 0 && (
                <details open className="card text-[11px]">
                  <summary className="cursor-pointer font-semibold">
                    Why each class is unsatisfiable
                  </summary>
                  <div className="mt-2 space-y-2">
                    {Object.entries(proposal.explanations).map(([cls, lines]) => (
                      <div key={cls}>
                        <div className="font-mono text-[12px]"><code>{cls}</code></div>
                        {lines.length === 0 && (
                          <div className="ml-3 text-muted">
                            — no asserted axioms reference it; likely
                            inferred via a chain of axioms.
                          </div>
                        )}
                        <ul className="ml-3 list-disc pl-3">
                          {lines.map((l) => (
                            <li key={l}><code className="font-mono">{l}</code></li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div>
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted">
                  Proposed actions ({actions.length})
                </div>
                {!proposal.needs_repair && (
                  <div className="card text-[12px] text-muted">
                    The reasoner did not flag any real issue. Nothing to do.
                  </div>
                )}
                {actions.map((a, i) => (
                  <div key={i} className="card mb-1 flex items-start justify-between gap-3 text-[11px]">
                    <div>
                      <div className="font-mono">
                        <span className="text-warn">{a.kind}</span>{" "}
                        <code>{a.target}</code>
                      </div>
                      <div className="text-muted">{a.reason}</div>
                      {a.details && Object.keys(a.details).length > 0 && (
                        <pre className="mt-1 overflow-auto rounded bg-bg/60 p-1 text-[10px]">
                          {JSON.stringify(a.details, null, 2)}
                        </pre>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn text-[11px]"
                      onClick={() => toggle(i)}
                      title="Drop this action from the repair"
                    >
                      Skip
                    </button>
                  </div>
                ))}
              </div>
              {apply.isError && <ErrorBox error={apply.error} />}
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
          <button type="button" className="btn text-xs" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1 text-xs"
            disabled={!proposal?.needs_repair || apply.isPending || actions.length === 0}
            onClick={() => apply.mutate()}
            title={
              !proposal?.needs_repair
                ? "No repair needed"
                : "Create a new DRAFT version with these actions applied"
            }
          >
            <ShieldCheck size={12} />
            {apply.isPending ? "Applying…" : "Confirm & create draft"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ShapeSetRow {
  id: string;
  workspace: string;
  ontology_version: string;
  name: string;
  description: string;
  turtle: string;
  enforcement: "warn" | "block" | "quarantine";
  active: boolean;
}

function downloadJson(value: unknown, filename: string) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(value, null, 2)], { type: "application/json" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

function ShaclPanel({ versionId }: { versionId: string }) {
  const ws = useApp((state) => state.workspaceId);
  const qc = useQueryClient();
  const [enforcement, setEnforcement] = useState<"warn" | "block" | "quarantine">("warn");
  const [shapeId, setShapeId] = useState<string | null>(null);
  const [shapeName, setShapeName] = useState("");
  const [shapeDescription, setShapeDescription] = useState("");
  const [shapeTurtle, setShapeTurtle] = useState("");
  const [sample, setSample] = useState(
    "@prefix ex: <https://example.com/> .\n\n" +
    "<urn:sample:1> a ex:ReplaceWithClass ;\n" +
    "  ex:replaceWithProperty \"value\" .\n",
  );
  const preview = useQuery({
    queryKey: ["shacl-preview", versionId],
    queryFn: () => rawFetch<string>(
      `/ontology/versions/${versionId}/auto-shapes/`,
      { headers: { Accept: "text/turtle" } },
      "text",
    ),
  });
  const version = useQuery({
    queryKey: ["ontology-version-status", versionId],
    queryFn: () => rawFetch<{ status: string; semver: string }>(`/ontology/versions/${versionId}/`),
  });
  const shapes = useQuery({
    queryKey: ["shape-sets", versionId],
    queryFn: async () => pageResults(await rawFetch<{ results?: ShapeSetRow[] } | ShapeSetRow[]>(
      `/semantic/shapes/?ontology_version=${versionId}&page_size=100`,
    )),
  });
  useEffect(() => {
    if (!shapeId && !shapeTurtle && preview.data) {
      setShapeName(`review/${version.data?.semver ?? versionId.slice(0, 8)}`);
      setShapeDescription("SME-reviewed constraints derived from the ontology draft.");
      setShapeTurtle(preview.data);
    }
  }, [preview.data, shapeId, shapeTurtle, version.data?.semver, versionId]);
  const persist = useMutation({
    mutationFn: () =>
      rawFetch<{ shape_set_id: string; name: string; enforcement: string }>(
        `/ontology/versions/${versionId}/auto-shapes/`,
        { method: "POST", body: JSON.stringify({ enforcement }) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shape-sets", versionId] }),
  });
  const saveShape = useMutation({
    mutationFn: () => rawFetch<ShapeSetRow>(shapeId ? `/semantic/shapes/${shapeId}/` : "/semantic/shapes/", {
      method: shapeId ? "PATCH" : "POST",
      body: JSON.stringify(shapeId ? {
        name: shapeName.trim(), description: shapeDescription.trim(), turtle: shapeTurtle, enforcement, active: true,
      } : {
        workspace: ws, ontology_version: versionId, name: shapeName.trim(), description: shapeDescription.trim(), turtle: shapeTurtle, enforcement, active: true,
      }),
    }),
    onSuccess: (saved) => {
      setShapeId(saved.id);
      qc.invalidateQueries({ queryKey: ["shape-sets", versionId] });
    },
  });
  const removeShape = useMutation({
    mutationFn: (id: string) => rawFetch(`/semantic/shapes/${id}/`, { method: "DELETE" }),
    onSuccess: () => {
      setShapeId(null); setShapeName(""); setShapeDescription(""); setShapeTurtle(preview.data ?? "");
      qc.invalidateQueries({ queryKey: ["shape-sets", versionId] });
    },
  });
  const validateSample = useMutation({
    mutationFn: () => rawFetch<ShaclSampleReport>(
      `/ontology/versions/${versionId}/sample-shacl/`,
      { method: "POST", body: JSON.stringify({ data_ttl: sample }) },
    ),
  });
  const isDraft = version.data?.status === "draft";

  function editShape(shape: ShapeSetRow) {
    setShapeId(shape.id); setShapeName(shape.name); setShapeDescription(shape.description);
    setShapeTurtle(shape.turtle); setEnforcement(shape.enforcement);
  }
  return (
    <div className="space-y-3">
      <div className="rounded border border-accent/30 bg-accent/5 p-3 text-xs">
        <div className="font-semibold text-text">Constraint workbench</div>
        <p className="mt-1 text-muted">Generated shapes are starting points. Edit, remove, or add constraints here; validate examples; then record SME review. Publication evaluates the final saved draft.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select className="input w-[10rem] text-xs"
                value={enforcement}
                onChange={(e) => setEnforcement(e.target.value as typeof enforcement)}>
          <option value="warn">enforcement: warn</option>
          <option value="block">enforcement: block</option>
          <option value="quarantine">enforcement: quarantine</option>
        </select>
        {isDraft && <button className="btn text-xs"
                onClick={() => persist.mutate()} disabled={persist.isPending}>
          {persist.isPending ? "Saving…" : "Regenerate from ontology"}
        </button>}
        <button className="btn text-xs"
                onClick={() =>
                  void downloadBlob(
                    `/ontology/versions/${versionId}/auto-shapes/`,
                    `shapes-${versionId.slice(0, 8)}.ttl`,
                  )
                }>
          <Download size={12} /> .ttl
        </button>
        <button className="btn text-xs" onClick={() => window.print()}><Printer size={12} /> Print review</button>
        {persist.isSuccess && (
          <span className="pill-good text-[11px]">saved: {persist.data.name}</span>
        )}
      </div>
      <div className="grid gap-3 lg:grid-cols-[220px_minmax(0,1fr)]">
        <div className="rounded border border-border bg-bg/40 p-2">
          <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase text-muted">
            <span>Saved shape sets</span>
            {isDraft && <button className="btn p-1" title="New shape set" onClick={() => { setShapeId(null); setShapeName(`review/${version.data?.semver ?? "draft"}`); setShapeDescription(""); setShapeTurtle(preview.data ?? ""); }}><Plus size={12} /></button>}
          </div>
          {shapes.isLoading ? <div className="text-xs text-muted">Loading…</div> : (shapes.data ?? []).length === 0 ? <div className="text-xs text-muted">No saved shapes for this version.</div> : <div className="space-y-1">{(shapes.data ?? []).map((shape) => <button key={shape.id} className={`block w-full rounded border px-2 py-2 text-left text-xs ${shape.id === shapeId ? "border-accent bg-accent/10" : "border-border"}`} onClick={() => editShape(shape)}><span className="block truncate font-medium">{shape.name}</span><span className="text-[10px] text-muted">{shape.enforcement} · {shape.active ? "active" : "inactive"}</span></button>)}</div>}
        </div>
        <div className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2"><input className="input text-xs" value={shapeName} onChange={(event) => setShapeName(event.target.value)} placeholder="Shape set name" disabled={!isDraft} /><input className="input text-xs" value={shapeDescription} onChange={(event) => setShapeDescription(event.target.value)} placeholder="Review scope and source" disabled={!isDraft} /></div>
          <textarea className="input min-h-[320px] font-mono text-[11px]" value={shapeTurtle} onChange={(event) => setShapeTurtle(event.target.value)} spellCheck={false} readOnly={!isDraft} aria-label="ShapeSet Turtle editor" />
          {isDraft && <div className="flex gap-2"><button className="btn-primary text-xs" disabled={saveShape.isPending || !ws || !shapeName.trim() || !shapeTurtle.trim()} onClick={() => saveShape.mutate()}><Save size={12} /> {saveShape.isPending ? "Saving…" : "Save shape set"}</button>{shapeId && <button className="btn text-xs text-bad" disabled={removeShape.isPending} onClick={() => { if (window.confirm("Delete this shape set? The ontology concepts are not deleted.")) removeShape.mutate(shapeId); }}><Trash2 size={12} /> Delete shape set</button>}</div>}
          {(saveShape.isError || removeShape.isError) && <ErrorBox error={saveShape.error ?? removeShape.error} />}
        </div>
      </div>
      {preview.isError && <ErrorBox error={preview.error} />}
      {persist.isError && <ErrorBox error={persist.error} />}
      <div className="border-t border-border pt-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div>
            <div className="text-xs font-semibold">Test sample instances</div>
            <div className="text-[11px] text-muted">Runs in memory. The sample is not persisted.</div>
          </div>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-1 text-xs"
            onClick={() => validateSample.mutate()}
            disabled={validateSample.isPending || !sample.trim()}
          >
            <Play size={12} /> {validateSample.isPending ? "Validating…" : "Validate sample"}
          </button>
        </div>
        <textarea
          className="input min-h-[180px] font-mono text-[11px]"
          value={sample}
          onChange={(event) => setSample(event.target.value)}
          spellCheck={false}
          aria-label="Sample instance Turtle"
        />
        {validateSample.isError && <div className="mt-2"><ErrorBox error={validateSample.error} /></div>}
        {validateSample.data && (
          <div className={`mt-2 rounded border p-3 text-xs ${(validateSample.data.passed ?? validateSample.data.conforms) ? "border-good/40 bg-good/5" : "border-bad/40 bg-bad/5"}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={(validateSample.data.passed ?? validateSample.data.conforms) ? "pill-good" : "pill-bad"}>
                {(validateSample.data.passed ?? validateSample.data.conforms)
                  ? (validateSample.data.shape_source === "pinned_core" ? "conforms with focus nodes" : "conforms")
                  : "validation gate not passed"}
              </span>
              <span className="pill">shapes: {validateSample.data.shape_source}</span>
              <span className="pill">violations: {validateSample.data.violation_count}</span>
              {validateSample.data.shape_source !== "pinned_core" && !validateSample.data.conforms && <button className="btn ml-auto text-[11px]" onClick={() => downloadJson(validateSample.data?.review_plan, `shacl-review-${versionId.slice(0, 8)}.json`)}><Download size={11} /> Review plan JSON</button>}
            </div>
            {validateSample.data.focus_counts && <div className="mt-2 space-y-1" aria-label="SHACL focus-node coverage">
              {validateSample.data.focus_counts.map((row) => <div key={row.shape} className="flex justify-between gap-3 border-b border-border py-1">
                <span className="truncate font-mono" title={row.shape}>{row.shape}</span><strong>{row.focus_nodes} focus nodes</strong>
              </div>)}
              {!validateSample.data.non_vacuous && <p className="text-bad">Vacuous pass: no shape matched a focus node. Publication is blocked.</p>}
            </div>}
            {validateSample.data.shape_source !== "pinned_core" && !validateSample.data.conforms && <ShaclReviewPlan plan={validateSample.data.review_plan} versionId={versionId} />}
            {validateSample.data.violations.length > 0 && (
              <details className="mt-3">
                <summary className="cursor-pointer text-[11px] text-muted">Raw SHACL evidence ({validateSample.data.violations.length})</summary>
              <div className="mt-2 space-y-2">
                {validateSample.data.violations.map((violation, index) => (
                  <div key={`${violation.focus_node}:${violation.result_path}:${index}`} className="rounded border border-border bg-bg/60 p-2 text-[11px]">
                    <div><span className="text-muted">Focus:</span> <code>{violation.focus_node}</code></div>
                    {violation.result_path && <div><span className="text-muted">Path:</span> <code>{violation.result_path}</code></div>}
                    <div className="mt-1 text-bad">{violation.message || "SHACL constraint violation"}</div>
                  </div>
                ))}
              </div>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ShaclReviewPlan({ plan, versionId }: { plan: ShaclSampleReport["review_plan"]; versionId: string }) {
  const ws = useApp((state) => state.workspaceId);
  const qc = useQueryClient();
  const [editingFocus, setEditingFocus] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [definition, setDefinition] = useState("");
  const [parent, setParent] = useState("");
  const entities = useQuery({
    queryKey: ["ontology-entities", versionId],
    queryFn: async () => pageResults(await rawFetch<{ results?: EntityRow[] } | EntityRow[]>(`/ontology/entities/?version=${versionId}&page_size=500`)),
  });
  const rows = entities.data ?? [];
  const activeConcept = plan.concepts.find((concept) => concept.focus_node === editingFocus);
  const activeEntity = rows.find((entity) => entity.source_iri === editingFocus || entity.semantic_iri === editingFocus);
  const save = useMutation({
    mutationFn: async () => {
      if (!activeConcept || !ws) throw new Error("Select a concept and workspace first.");
      const entity = activeEntity
        ? await rawFetch<EntityRow>(`/ontology/entities/${activeEntity.id}/`, { method: "PATCH", body: JSON.stringify({ name: label.trim() || activeConcept.display_name, description: definition.trim(), parent: parent || null }) })
        : await rawFetch<EntityRow>("/ontology/entities/", { method: "POST", body: JSON.stringify({ workspace: ws, version: versionId, source_iri: activeConcept.focus_node, name: label.trim() || activeConcept.display_name, description: definition.trim(), parent: parent || null }) });
      const saveLexical = async (predicate: "label" | "definition", value: string) => {
        if (!value.trim()) return;
        const result = pageResults(await rawFetch<{ results?: LexicalRow[] } | LexicalRow[]>(`/ontology/lexical-entries/?version=${versionId}&subject_iri=${encodeURIComponent(activeConcept.focus_node)}&predicate=${predicate}&language=en&page_size=20`));
        const existing = result[0];
        await rawFetch(existing ? `/ontology/lexical-entries/${existing.id}/` : "/ontology/lexical-entries/", {
          method: existing ? "PATCH" : "POST",
          body: JSON.stringify(existing ? { value: value.trim() } : { workspace: ws, version: versionId, subject_iri: activeConcept.focus_node, predicate, language: "en", value: value.trim() }),
        });
      };
      await saveLexical("label", label.trim() || activeConcept.display_name);
      await saveLexical("definition", definition);
      return entity;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ontology-entities", versionId] });
      qc.invalidateQueries({ queryKey: ["ontology-graph", versionId] });
      setEditingFocus(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => rawFetch(`/ontology/entities/${id}/`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ontology-entities", versionId] });
      setEditingFocus(null);
    },
  });

  function open(concept: ShaclSampleReport["review_plan"]["concepts"][number]) {
    const entity = rows.find((row) => row.source_iri === concept.focus_node || row.semantic_iri === concept.focus_node);
    setEditingFocus(concept.focus_node);
    setLabel(entity?.name ?? concept.display_name);
    setDefinition(entity?.description ?? "");
    setParent(entity?.parent ?? "");
  }

  return (
    <div className="mt-3 space-y-2">
      <div className="rounded border border-border bg-bg/60 p-3">
        <div className="flex flex-wrap items-center gap-2 text-[11px]"><span className="font-semibold text-text">SME review inventory</span><span className="pill">{plan.concept_count} concepts</span><span className="pill">{plan.raw_finding_count} findings</span></div>
        <p className="mt-1 text-[11px] text-muted">{plan.principle}</p>
      </div>
      {plan.concepts.map((concept) => {
        const exists = rows.some((row) => row.source_iri === concept.focus_node || row.semantic_iri === concept.focus_node);
        return <div key={concept.focus_node} className="rounded border border-border bg-bg/60 p-3 text-[11px]">
          <div className="flex items-start justify-between gap-3"><div className="min-w-0"><div className="font-semibold text-text">{concept.display_name}</div><code className="block truncate text-[10px] text-muted" title={concept.focus_node}>{concept.focus_node}</code></div><button className="btn shrink-0 text-[11px]" onClick={() => open(concept)}><Pencil size={11} /> {exists ? "Edit concept" : "Create concept"}</button></div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">{concept.actions.map((action, index) => <div key={`${action.code}-${index}`} className="rounded border border-border px-2 py-2"><div className="font-medium text-text">{action.title}</div><div className="mt-0.5 text-muted">{action.guidance}</div><div className="mt-1 text-[10px] text-bad">{action.message}</div></div>)}</div>
        </div>;
      })}
      {activeConcept && <div className="rounded border border-accent/40 bg-accent/5 p-3">
        <div className="mb-2 flex items-center justify-between"><div className="text-xs font-semibold">{activeEntity ? "Edit" : "Create"} {activeConcept.display_name}</div><button className="btn p-1" onClick={() => setEditingFocus(null)}><X size={12} /></button></div>
        <div className="grid gap-2 sm:grid-cols-2"><input className="input text-xs" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Approved English label" /><select className="input text-xs" value={parent} onChange={(event) => setParent(event.target.value)}><option value="">No parent / decide later</option>{rows.filter((row) => row.id !== activeEntity?.id).map((row) => <option key={row.id} value={row.id}>{row.name}</option>)}</select></div>
        <textarea className="input mt-2 min-h-[90px] text-xs" value={definition} onChange={(event) => setDefinition(event.target.value)} placeholder="SME-approved definition, scope, exclusions, and source" />
        <div className="mt-2 flex flex-wrap gap-2"><button className="btn-primary text-xs" disabled={save.isPending || !label.trim()} onClick={() => save.mutate()}><Save size={11} /> Save starting point</button>{activeEntity && <button className="btn text-xs text-bad" disabled={remove.isPending} onClick={() => { if (window.confirm(`Delete ${activeEntity.name} from this draft? Constraints that target its IRI remain until edited separately.`)) remove.mutate(activeEntity.id); }}><Trash2 size={11} /> Delete concept</button>}</div>
        {(save.isError || remove.isError) && <div className="mt-2"><ErrorBox error={save.error ?? remove.error} /></div>}
      </div>}
    </div>
  );
}

function CqPanel({ versionId }: { versionId: string }) {
  const qc = useQueryClient();
  const ws = useApp((state) => state.workspaceId);
  const { canEdit } = useWorkspacePermissions();
  const [askText, setAskText] = useState("");
  const [editing, setEditing] = useState<CqRow | null>(null);
  const [authoring, setAuthoring] = useState(false);
  const [text, setText] = useState("");
  const [sparql, setSparql] = useState("");
  const [expectedKind, setExpectedKind] = useState("nonempty");
  const version = useQuery({
    queryKey: ["ontology-version-status", versionId],
    queryFn: () => rawFetch<{ status: string }>(`/ontology/versions/${versionId}/`),
  });
  const list = useQuery({
    queryKey: ["cqs", versionId],
    queryFn: () =>
      rawFetch<{ count: number; results: CqRow[] }>(
        `/ontology/competency-questions/?version=${versionId}&page_size=100`,
      ),
  });
  const termProfile = useQuery<{ profile_id: string | null; term_count: number; copied_rows: number }>({
    queryKey: ["ontology-term-retrieval", versionId],
    queryFn: () => rawFetch(`/ontology/versions/${versionId}/term-retrieval/`),
  });
  const generate = useMutation<{ total: number; created: number; existing: number }>({
    mutationFn: () => rawFetch(`/ontology/versions/${versionId}/generate-term-cqs/`,
      { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cqs", versionId] }),
  });
  const enableTerms = useMutation({
    mutationFn: () => rawFetch(`/ontology/versions/${versionId}/term-retrieval/`,
      { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ontology-term-retrieval", versionId] }),
  });
  const ask = useMutation<{ citations: Array<{ id: string; title?: string; definition?: string; uri?: string; score: number }> }>({
    mutationFn: () => rawFetch(`/retrieval/profiles/${termProfile.data?.profile_id}/query/`,
      { method: "POST", body: JSON.stringify({ query: askText.trim() }) }),
  });
  const runOne = useMutation({
    mutationFn: (id: string) =>
      rawFetch<CqRow>(`/ontology/competency-questions/${id}/run/`, { method: "POST", body: "{}" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cqs", versionId] }),
  });
  const runAll = useMutation({
    mutationFn: () =>
      rawFetch<{ passed: number; failed: number; untested: number; total: number }>(
        `/ontology/versions/${versionId}/run-cqs/`,
        { method: "POST", body: "{}" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cqs", versionId] });
      qc.invalidateQueries({ queryKey: ["ontology-release-readiness", versionId] });
    },
  });
  const save = useMutation({
    mutationFn: () => rawFetch<CqRow>(
      editing ? `/ontology/competency-questions/${editing.id}/` : "/ontology/competency-questions/",
      {
        method: editing ? "PATCH" : "POST",
        body: JSON.stringify(editing ? {
          text: text.trim(), sparql: sparql.trim(), expected_kind: expectedKind,
        } : {
          workspace: ws, version: versionId,
          text: text.trim(), sparql: sparql.trim(), expected_kind: expectedKind,
        }),
      },
    ),
    onSuccess: () => {
      setAuthoring(false); setEditing(null); setText(""); setSparql(""); setExpectedKind("nonempty");
      qc.invalidateQueries({ queryKey: ["cqs", versionId] });
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => rawFetch(`/ontology/competency-questions/${id}/`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cqs", versionId] }),
  });
  const rows = list.data?.results ?? [];
  const isDraft = version.data?.status === "draft";

  function openEditor(row?: CqRow) {
    setEditing(row ?? null);
    setText(row?.text ?? "");
    setSparql(row?.sparql ?? "");
    setExpectedKind(row?.expected_kind ?? "nonempty");
    setAuthoring(true);
  }

  return (
    <div className="space-y-3">
      <section className="card space-y-2 text-xs" aria-label="Term-set tests and retrieval">
        <h3 className="font-semibold">Test approved terms against this draft</h3>
        <p className="text-muted">Generate deterministic questions from pinned term sets, then run them against the saved ontology. Outcomes stay on this draft.</p>
        <div className="flex flex-wrap items-center gap-2">
          {isDraft && canEdit && <button className="btn" disabled={generate.isPending} onClick={() => generate.mutate()}>
            {generate.isPending ? "Generating…" : "Generate term questions"}</button>}
          {generate.data && <span role="status">{generate.data.created} new questions; {generate.data.existing} already present.</span>}
        </div>
        {generate.isError && <ErrorBox error={generate.error} />}
        <div className="border-t border-border pt-2">
          <p className="text-muted">Ask a question of approved term definitions. This is an explicit opt-in; it reads pinned versions without copying text into Documents, Chunks or vectors.</p>
          {termProfile.data && <p className="mt-1">{termProfile.data.term_count} pinned terms · {termProfile.data.profile_id ? "definition search enabled" : "nothing indexed"}</p>}
          {termProfile.data?.profile_id && <a className="mt-2 inline-block text-accent underline"
            href={`/retrieval?profile=${encodeURIComponent(termProfile.data.profile_id)}&ontology=${encodeURIComponent(versionId)}`}>
            Test retrieval for this draft →</a>}
          {termProfile.data && !termProfile.data.profile_id && canEdit && <button className="btn mt-2" disabled={!termProfile.data.term_count || enableTerms.isPending}
            onClick={() => enableTerms.mutate()}>Index my term definitions</button>}
          {termProfile.data?.profile_id && <div className="mt-2 flex gap-2">
            <input className="input flex-1" value={askText} maxLength={10000} onChange={(event) => setAskText(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && askText.trim()) ask.mutate(); }}
              placeholder="What is a policy?" aria-label="Ask a question about approved terms" />
            <button className="btn-primary" disabled={!askText.trim() || ask.isPending} onClick={() => ask.mutate()}>Ask</button>
          </div>}
          {ask.data && <div role="status" className="mt-2 space-y-1">
            <p>{ask.data.citations.length} matches with citations.</p>
            {ask.data.citations.map((hit) => <div key={hit.id} className="border-l-2 border-accent pl-2">
              <strong>{hit.title ?? "Approved term"}</strong> <span className="text-muted">{hit.definition}</span>
              <div className="break-all font-mono text-[10px] text-muted">{hit.uri}</div>
            </div>)}
          </div>}
          {[termProfile.error, enableTerms.error, ask.error].filter(Boolean).map((error, index) =>
            <p key={index} role="alert" className="mt-1 text-bad">{error?.message}</p>)}
        </div>
      </section>
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted">
          Each CQ carries a SPARQL probe and an expected outcome.
          The pack is ready when every CQ passes against the published version.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn inline-flex items-center gap-1 text-xs" onClick={() => window.print()}>
            <Printer size={12} /> Print
          </button>
          <button className="btn inline-flex items-center gap-1 text-xs" disabled={rows.length === 0} onClick={() => downloadJson({ version: versionId, competency_questions: rows }, `competency-questions-${versionId.slice(0, 8)}.json`)}>
            <Download size={12} /> JSON
          </button>
          {isDraft && (
            <button className="btn inline-flex items-center gap-1 text-xs" onClick={() => openEditor()}>
              <Plus size={12} /> Add CQ
            </button>
          )}
          <button className="btn-primary inline-flex items-center gap-1 text-xs"
                  onClick={() => runAll.mutate()} disabled={runAll.isPending || rows.length === 0}>
            <Play size={12} /> {runAll.isPending ? "Running…" : "Run all"}
          </button>
        </div>
      </div>
      {authoring && isDraft && (
        <div className="card space-y-3 border-accent/30">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">{editing ? "Edit competency question" : "New competency question"}</div>
            <button type="button" className="btn p-1" onClick={() => setAuthoring(false)} aria-label="Close CQ editor"><X size={13} /></button>
          </div>
          <label className="block">
            <div className="mb-1 text-[11px] uppercase text-muted">Question</div>
            <input className="input text-sm" value={text} onChange={(event) => setText(event.target.value)} placeholder="Which policies have an active claim?" />
          </label>
          <label className="block">
            <div className="mb-1 text-[11px] uppercase text-muted">Expected result</div>
            <select className="input w-52 text-xs" value={expectedKind} onChange={(event) => setExpectedKind(event.target.value)}>
              <option value="nonempty">Non-empty SELECT result</option>
              <option value="ask">ASK must be true</option>
              <option value="any">Smoke test only</option>
            </select>
          </label>
          <label className="block">
            <div className="mb-1 text-[11px] uppercase text-muted">SPARQL probe</div>
            <textarea className="input min-h-[150px] font-mono text-[11px]" value={sparql} onChange={(event) => setSparql(event.target.value)} spellCheck={false} />
          </label>
          {save.isError && <ErrorBox error={save.error} />}
          <button className="btn-primary inline-flex items-center gap-1 text-xs" onClick={() => save.mutate()} disabled={save.isPending || !ws || !text.trim() || !sparql.trim()}>
            <Save size={12} /> {save.isPending ? "Saving…" : "Save CQ"}
          </button>
        </div>
      )}
      {runAll.isSuccess && (
        <div className="card text-[11px]">
          <span className="pill-good">passed: {runAll.data.passed}</span>{" "}
          <span className="pill-bad">failed: {runAll.data.failed}</span>{" "}
          <span className="pill-warn">untested: {runAll.data.untested}</span>{" "}
          <span className="pill">total: {runAll.data.total}</span>
        </div>
      )}
      {list.isError && <ErrorBox error={list.error} />}
      {runAll.isError && <ErrorBox error={runAll.error} />}
      {list.data && list.data.count > rows.length && <p className="text-xs text-muted">Showing the first {rows.length} of {list.data.count} questions; Run all covers every saved question.</p>}
      {rows.length === 0 && (
        <div className="card text-xs text-muted">
          No competency questions attached to this version. Use the wizard to draft them.
        </div>
      )}
      {rows.map((c) => (
        <div key={c.id} className="card text-xs">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="font-medium">{c.text}</div>
              <div className="mt-1 text-[11px] text-muted">
                expected: <code>{c.expected_kind}</code>
                {c.last_run_at && <> · last run: {new Date(c.last_run_at).toLocaleString()}</>}
                {c.last_result_count != null && <> · rows: {c.last_result_count}</>}
              </div>
              {c.last_error && <div className="mt-1 text-[11px] text-bad">Error: {c.last_error}</div>}
              {c.sparql && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-[11px] text-muted">show SPARQL</summary>
                  <pre className="mt-1 overflow-auto rounded bg-bg/60 p-2 text-[10px]">{c.sparql}</pre>
                </details>
              )}
            </div>
            <div className="flex items-center gap-2">
              <StatusPill status={c.status} />
              {isDraft && (
                <button className="btn p-1" onClick={() => openEditor(c)} title="Edit CQ" aria-label={`Edit ${c.text}`}>
                  <Pencil size={11} />
                </button>
              )}
              {isDraft && (
                <button
                  className="btn p-1 text-bad"
                  onClick={() => { if (window.confirm("Delete this competency question?")) remove.mutate(c.id); }}
                  title="Delete CQ"
                  aria-label={`Delete ${c.text}`}
                >
                  <Trash2 size={11} />
                </button>
              )}
              <button className="btn text-xs"
                      onClick={() => runOne.mutate(c.id)}
                      disabled={runOne.isPending && runOne.variables === c.id}>
                <Play size={10} /> Run
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SparqlPanel({ versionId }: { versionId: string }) {
  const [q, setQ] = useState(
    "PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>\n" +
    "PREFIX owl: <http://www.w3.org/2002/07/owl#>\n" +
    "SELECT ?cls ?label WHERE {\n" +
    "  ?cls a owl:Class .\n" +
    "  OPTIONAL { ?cls rdfs:label ?label }\n" +
    "} LIMIT 50",
  );
  const m = useMutation({
    mutationFn: () =>
      rawFetch<SparqlResp>(`/ontology/versions/${versionId}/sparql/`, {
        method: "POST", body: JSON.stringify({ query: q, limit: 500 }),
      }),
  });
  const data = m.data;
  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        Read-only SPARQL against the version's TBox graph
        (INSERT/DELETE/DROP/CLEAR/LOAD/CREATE are rejected).
      </p>
      <textarea className="input min-h-[160px] font-mono text-[11px]"
                value={q} onChange={(e) => setQ(e.target.value)} />
      <div>
        <button className="btn-primary text-xs"
                onClick={() => m.mutate()} disabled={m.isPending}>
          <Play size={12} /> {m.isPending ? "Running…" : "Run query"}
        </button>
      </div>
      {m.isError && <ErrorBox error={m.error} />}
      {data && data.kind === "ask" && (
        <div className="card text-xs">
          ASK ⇒ <span className={data.boolean ? "pill-good" : "pill-bad"}>{String(data.boolean)}</span>
        </div>
      )}
      {data && data.kind === "select" && (
        <div className="card overflow-x-auto p-0">
          <table className="min-w-full text-[11px]">
            <thead className="bg-border/30 text-left text-[10px] uppercase tracking-wide text-muted">
              <tr>{data.columns.map((c) => <th key={c} className="px-3 py-2 font-medium">{c}</th>)}</tr>
            </thead>
            <tbody>
              {data.rows.map((row, i) => (
                <tr key={i} className="border-t border-border">
                  {data.columns.map((c) => (
                    <td key={c} className="px-3 py-1.5 font-mono text-[10px]">{row[c] ?? ""}</td>
                  ))}
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={data.columns.length} className="px-3 py-3 text-center text-muted">— no rows —</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <details open className="card">
      <summary className="cursor-pointer text-xs font-semibold">{title}</summary>
      <div className="mt-2 space-y-1">{children}</div>
    </details>
  );
}

